import React, { useEffect, useState, useMemo } from 'react'
import {
  Button,
  DatePicker,
  Form,
  Spin,
  Table,
  Tabs,
  Select,
  Tag,
  Card,
  Row,
  Col,
  Statistic,
  Input,
  Space,
  Tooltip,
  Badge,
  Alert,
  message
} from 'antd'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import InstitutionUserService from '../../services/InstitutionUserService'

import {
  LoadingOutlined,
  BarChartOutlined,
  DollarOutlined,
  SearchOutlined,
  DownloadOutlined,
  FileTextOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  FilterOutlined,
  ReloadOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DownOutlined,
  UpOutlined,
  SyncOutlined
} from '@ant-design/icons'
import { saveAs } from 'file-saver'
import { jsPDF } from 'jspdf'
import * as XLSX from 'xlsx'
import type { ColumnsType, SorterResult } from 'antd/es/table/interface'

const antIcon = <LoadingOutlined style={{ fontSize: 'var(--base-font-size)', color: '#37762F' }} spin />
const { RangePicker } = DatePicker

interface DataType {
  key: string | number
  index: number
  bank_code: string
  transaction_reference: string
  debit_account_number: string
  debit_account_name: string
  beneficiary_account_name: string
  beneficiary_bank_name: string
  beneficiary_account_number: string
  transaction_currency: string
  amount: number
  reason: string
  status: string
  created_at: string
  updated_at: string
  phone_number?: string
  email?: string
  link?: string
}

interface JSPADataType {
  key: string | number
  index: number
  // Bailiff Refund Request Info
  bailiff_refund_request_id: string
  bank_code: string
  transaction_reference: string
  debit_account_number: string
  debit_account_name: string
  beneficiary_account_name: string
  beneficiary_bank_name: string
  beneficiary_account_number: string
  amount: string
  transaction_currency: string
  reason: string
  bailiff_assignee: string
  bailiff_national_id: string
  bailiff_email: string
  bailiff_phone_number: string
  property_id: string
  execution_case_number: string
  attachment: string
  status: string
  created_by: string
  otp: string
  otp_expiration: string
  created_at: string
  updated_at: string
  // Bidder Info
  bidder_id: string
  bidder_full_name: string
  bidder_nid: string
  bidder_phone_number: string
  bidder_email: string
  // Bid Security Info
  bid_security_id: string
  bid_security_ref_number: string
  bid_security_type: string
  bid_security_amount_covered: string
  bid_security_payment_id: string
  bid_security_created_at: string
  table_source: string // 'bailiff_refund_request' or 'refund_request'
}

interface FilterParams {
  from: string
  to: string
  status: string
  search?: string
  bank?: string
  tableSource?: string
  report_type?: string
  sort_by?: string
  sort_order?: string
  page?: number
  limit?: number
}

interface JSPAStats {
  totalRequests: number
  totalAmount: number
  pendingRequests: number
  approvedRequests: number
  rejectedRequests: number
  receivedRequests: number
  averageAmount: number
  completionRate: number
}

interface JSPASummary {
  byStatus: Record<string, number>
  byBank: Record<string, number>
  byTableSource: Record<string, number>
  monthlyTrend: Array<{ month: string, count: number, amount: number }>
}

interface ComprehensiveReportDataType {
  key: string | number
  index: number
  // Common fields
  table_source: string // 'refund_request', 'bailiff_refund_request', 'bid_security_payment'
  request_type: string // 'refund', 'bailiff', 'bid_security'
  status: string
  amount: string | number
  created_at: string
  updated_at: string

  // Refund Request fields
  refund_request_id?: string
  bailiff_refund_request_id?: string
  bank_code?: string
  transaction_reference?: string
  debit_account_number?: string
  debit_account_name?: string
  beneficiary_account_name?: string
  beneficiary_bank_name?: string
  beneficiary_account_number?: string
  transaction_currency?: string
  reason?: string
  phone_number?: string
  email?: string

  // Bailiff-specific fields
  bailiff_assignee?: string
  bailiff_national_id?: string
  bailiff_email?: string
  bailiff_phone_number?: string
  property_id?: string
  execution_case_number?: string
  attachment?: string
  created_by?: string
  otp?: string
  otp_expiration?: string

  // Bidder Info
  bidder_id?: string
  bidder_full_name?: string
  bidder_nid?: string
  bidder_phone_number?: string
  bidder_email?: string

  // Bid Security Info
  bid_security_id?: string
  bid_security_ref_number?: string
  bid_security_type?: string
  bid_security_amount_covered?: string
  bid_security_payment_id?: string
  bid_security_created_at?: string

  // Bid Security Payment fields
  bs_id?: string
  ref_number?: string
  type?: string
  amount_covered?: string
  bs_expiration_date?: string
  used?: boolean
  payment_id?: string
  currency?: string
  payment_date_time?: string
  payer_code?: string
  payer_name?: string
  payment_channel?: string
  full_name?: string
  nid?: string
}

/** Rows from GET /refund/bidders-not-requested-bid-security-report */
interface NotRequestedBidSecurityRow {
  key: string | number
  index: number
  bid_security_ref_number?: string
  /** Same value as bid_security_ref_number; matches refund_request / bailiff_refund_request transaction_reference. */
  transaction_reference?: string
  /** Bid security reference (same as transaction_reference for refund matching). */
  reference_number?: string
  bs_id?: string
  payment_id?: string
  payment_amount?: string | number
  currency?: string
  payment_date_time?: string
  bidder_id?: string
  bidder_full_name?: string
  bidder_phone_number?: string
  bidder_email?: string
  bidder_nid?: string
  property_id?: string
  execution_case_no?: string
  payer_code?: string
  auction_status?: string
}

interface ComprehensiveReportMeta {
  page: number
  pages: number
  total: number
  total_amount: number
  total_count: number
  stats: {
    totalRequests: number
    totalAmount: number
    pendingRequests: number
    approvedRequests: number
    rejectedRequests: number
    receivedRequests: number
    averageAmount: number
    completionRate: number
    totalAmountRefundRequests: number
    totalAmountRefundRequestsReceived: number
    pendingRefundRequestAmount: number
    totalAmountDownloadedForRefund: number
    totalFailedRequestAmount: number
    allTotalAmountPaidFromBidSecurity: number
    remainAmountToBeRefunded: number
    percentageAmountRefunded: number
    percentageAmountRemain: number
    processedVsReceived: number
    downloadedToBeRefundedRequests: number
    totalBidSecurities: number
    failedRequests: number
    approvedRequestsCount: number
    rejectedRequestsCount: number
  }
  status_breakdown: Record<string, number>
  bank_breakdown: Record<string, number>
  table_source_breakdown: Record<string, number>
  filters_applied: {
    search?: string
    status?: string
    report_type?: string
    from?: string
    to?: string
    sort_by?: string
    sort_order?: string
  }
}

type TableDataType = DataType | JSPADataType | ComprehensiveReportDataType

const Reports: React.FC = () => {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 })
  const [biddersData, setBiddersData] = useState<DataType[]>([])
  const [bailiffsData, setBailiffsData] = useState<DataType[]>([])
  const [bidSecuritiesData, setBidSecuritiesData] = useState<DataType[]>([])
  const [jspaData, setJspaData] = useState<JSPADataType[]>([])
  const [activeTab, setActiveTab] = useState('comprehensive')
  const [filterStatus, setFilterStatus] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [totals, setTotals] = useState<any>(null)
  const [count, setCount] = useState(0)
  const [sortedInfo, setSortedInfo] = useState<SorterResult<DataType>>({})
  const [tabLoading, setTabLoading] = useState<Record<string, boolean>>({})
  const [lastDataFetch, setLastDataFetch] = useState<Record<string, number>>({})
  const userInfo = JSON.parse(localStorage.getItem('userInfo') ?? '{}')

  // Enhanced JSPA state
  const [filteredJspaData, setFilteredJspaData] = useState<JSPADataType[]>([])
  const [jspaStats, setJspaStats] = useState<JSPAStats>({
    totalRequests: 0,
    totalAmount: 0,
    pendingRequests: 0,
    approvedRequests: 0,
    rejectedRequests: 0,
    receivedRequests: 0,
    averageAmount: 0,
    completionRate: 0
  })
  const [jspaSummary, setJspaSummary] = useState<JSPASummary>({
    byStatus: {},
    byBank: {},
    byTableSource: {},
    monthlyTrend: []
  })
  const [searchText, setSearchText] = useState('')
  const [selectedStatus, setSelectedStatus] = useState<string>('')
  const [selectedBank, setSelectedBank] = useState<string>('')
  const [selectedTableSource, setSelectedTableSource] = useState<string>('')
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)

  // Comprehensive Report state
  const [comprehensiveData, setComprehensiveData] = useState<ComprehensiveReportDataType[]>([])
  const [comprehensiveMeta, setComprehensiveMeta] = useState<ComprehensiveReportMeta | null>(null)
  const [selectedReportType, setSelectedReportType] = useState<string>('all')
  const [sortBy, setSortBy] = useState<string>('created_at')
  const [sortOrder, setSortOrder] = useState<string>('DESC')
  const [comprehensiveStats, setComprehensiveStats] = useState<any>(null)
  const [statusBreakdown, setStatusBreakdown] = useState<Record<string, number>>({})
  const [bankBreakdown, setBankBreakdown] = useState<Record<string, number>>({})
  const [notRequestedBidSecurityData, setNotRequestedBidSecurityData] = useState<NotRequestedBidSecurityRow[]>([])
  const [tableSourceBreakdown, setTableSourceBreakdown] = useState<Record<string, number>>({})
  const [showBankDistribution, setShowBankDistribution] = useState(false)
  const [selectedDataSourceTab, setSelectedDataSourceTab] = useState<string>('all')
  const [tabData, setTabData] = useState<Record<string, any[]>>({})
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  // Download functions
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  /**
   * Export Refund Data to CSV with Header and Proper Formatting
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const downloadCSV = (data: DataType[]) => {
    // Create a header row with the current date
    const currentDate = new Date().toLocaleDateString()
    const headerTitle = `Refund Report from ${currentDate}`

    // Define column headers
    const columnHeaders = ['Index', 'Account Name', 'Bank Name', 'Account Number', 'Amount', 'Updated Date']

    // Map through the data and only pick the predefined columns in the right order
    const filteredData = data.map((item, index) => [
      index + 1, // Adding index starting from 1
      item.beneficiary_account_name,
      item.beneficiary_bank_name,
      item.beneficiary_account_number,
      item.amount.toLocaleString(), // Format the amount with commas
      (item.updated_at !== '') ? new Date(item.updated_at).toLocaleString() : '' // Format the date
    ])

    // Helper function to escape CSV fields properly
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const escapeCSV = (field: any) => {
      const fieldStr = String(field)
      // If the field contains commas, quotes, or newlines, wrap it in quotes and escape any quotes
      if (fieldStr.includes(',') || fieldStr.includes('"') || fieldStr.includes('\n')) {
        return `"${fieldStr.replace(/"/g, '""')}"`
      }
      return fieldStr
    }

    // Build the CSV content
    let csvContent = `${escapeCSV(headerTitle)}\n\n` // Add title and an empty line

    // Add column headers
    csvContent += columnHeaders.map(header => escapeCSV(header)).join(',') + '\n'

    // Add data rows
    filteredData.forEach(row => {
      csvContent += row.map(cell => escapeCSV(cell)).join(',') + '\n'
    })

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' })
    saveAs(blob, 'refunds_report.csv')
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  /**
   * Export Refund Data to Excel with Enhanced Styling
   */

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const downloadExcelFile = (data: DataType[], filename: string) => {
    // Map through the data and only pick the predefined columns
    const filteredData = data.map((item, index) => ({
      index: index + 1, // Adding index starting from 1
      beneficiary_account_name: item.beneficiary_account_name,
      beneficiary_bank_name: item.beneficiary_bank_name,
      beneficiary_account_number: item.beneficiary_account_number,
      amount: item.amount,
      updated_at: (item.updated_at !== '') ? new Date(item.updated_at).toLocaleString() : '' // Format the date if necessary
    }))

    // Convert the filtered data to a worksheet starting from A3
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    const ws = XLSX.utils.json_to_sheet(filteredData, { origin: 'A3' })

    // Get current date in desired format
    const currentDate = new Date().toLocaleDateString()

    // Add title at A1
    XLSX.utils.sheet_add_aoa(ws, [['Refund Report from ' + currentDate]], { origin: 'A1' })

    // Merge cells for header (A1 to F1)
    if (ws['!merges'] == null) ws['!merges'] = []
    ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }) // Merge from A1 to F1

    // Style the worksheet
    // Define column widths
    if (ws['!cols'] == null) ws['!cols'] = []
    ws['!cols'][0] = { wch: 10 } // Index column
    ws['!cols'][1] = { wch: 25 } // Account name
    ws['!cols'][2] = { wch: 20 } // Bank name
    ws['!cols'][3] = { wch: 20 } // Account number
    ws['!cols'][4] = { wch: 15 } // Amount
    ws['!cols'][5] = { wch: 20 } // Updated date

    // Apply cell styles
    // Header title style
    ws.A1 = {
      v: 'Refund Report from ' + currentDate,
      t: 's',
      s: {
        font: { bold: true, sz: 16, name: 'Arial', color: { rgb: 'FFFFFF' } },
        alignment: { horizontal: 'center', vertical: 'center' },
        fill: { fgColor: { rgb: '4472C4' } } // Blue background
      }
    }

    // Style the table headers in row 3
    const headerCells = ['A3', 'B3', 'C3', 'D3', 'E3', 'F3']
    const headerNames = ['Index', 'Account Name', 'Bank Name', 'Account Number', 'Amount', 'Updated Date']

    headerCells.forEach((cell, index) => {
      ws[cell] = {
        v: headerNames[index],
        t: 's',
        s: {
          font: { bold: true, sz: 12, name: 'Arial' },
          alignment: { horizontal: 'center' },
          fill: { fgColor: { rgb: 'D9E1F2' } }, // Light blue background
          border: {
            top: { style: 'thin' },
            bottom: { style: 'thin' },
            left: { style: 'thin' },
            right: { style: 'thin' }
          }
        }
      }
    })

    // Create a new workbook
    const wb = XLSX.utils.book_new()

    // Append the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Refunds')

    // Write the file to download with styles
    XLSX.writeFile(wb, filename, { bookType: 'xlsx', bookSST: false, type: 'binary' })
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const createNotRequestedBidSecurityPdf = (data: any[], title: string): void => {
    // eslint-disable-next-line new-cap -- jsPDF export is lowercase
    const doc = new jsPDF('landscape')
    doc.setFontSize(14)
    doc.text(title, 14, 14)
    doc.setFontSize(8)
    let y = 22
    const maxRows = 45
    data.slice(0, maxRows).forEach((row) => {
      const line = [
        String(row.reference_number ?? row.transaction_reference ?? row.bid_security_ref_number ?? ''),
        String(row.bidder_full_name ?? ''),
        String(row.property_id ?? ''),
        String(row.payment_amount ?? ''),
        row.payment_date_time != null ? new Date(row.payment_date_time).toLocaleString() : '',
        String(row.auction_status ?? '')
      ].join(' | ')
      const lines = doc.splitTextToSize(line, 270)
      lines.forEach((ln: string) => {
        if (y > 190) {
          doc.addPage()
          y = 14
        }
        doc.text(ln, 14, y)
        y += 4
      })
    })
    doc.save(`No_Bid_Security_Refund_Request_${new Date().toISOString().split('T')[0]}.pdf`)
  }

  // Enhanced download functions with filters and predefined headers
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const downloadFilteredCSV = async (): Promise<void> => {
    try {
      // Get current filters
      const filters = {
        from: fromDate,
        to: toDate,
        status: filterStatus,
        report_type: selectedReportType,
        sort_by: sortBy,
        sort_order: sortOrder,
        limit: 10000, // Get all data without pagination
        page: 1,
        search: ''
      }

      // eslint-disable-next-line no-console
      console.log('🔍 Download CSV - Current filters:', filters)
      // eslint-disable-next-line no-console
      console.log('🔍 Download CSV - Active tab:', activeTab)
      // eslint-disable-next-line no-console
      console.log('🔍 Download CSV - Filter values:', {
        fromDate,
        toDate,
        filterStatus,
        selectedReportType,
        sortBy,
        sortOrder
      })

      // Fetch data based on active tab
      let data: any[] = []
      let filename = ''
      let headers: string[] = []

      // sanitize empty filters (backend treats empty string as filter)
      const from = (filters.from != null && filters.from !== '') ? filters.from : undefined
      const to = (filters.to != null && filters.to !== '') ? filters.to : undefined
      const status = (filters.status != null && filters.status !== '') ? filters.status : undefined

      if (activeTab === 'comprehensive') {
        const response = await InstitutionUserService.GetComprehensiveReportData({ ...filters, from, to, status })
        const raw = response
        data = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : Array.isArray(raw?.data?.data)
              ? raw.data.data
              : Array.isArray(raw?.items)
                ? raw.items
                : []
        // Exclude bid and bid_security_payment from downloads
        data = data.filter((item: any) => item.table_source === 'refund_request' || item.table_source === 'bailiff_refund_request')
        // eslint-disable-next-line no-console
        console.log('🔍 Comprehensive response:', response)
        // eslint-disable-next-line no-console
        console.log('🔍 Comprehensive data length:', data.length)
        filename = `Comprehensive_Report_${new Date().toISOString().split('T')[0]}.csv`
        headers = [
          'Status',
          'Amount (RWF)',
          'Created Date',
          'Transaction Reference',
          'Beneficiary Account Name',
          'Beneficiary Bank Name',
          'Beneficiary Account Number',
          'Phone Number'
        ]
      } else if (activeTab === 'bidders') {
        try {
          const response = await InstitutionUserService.SearchWithParameters(filters.limit ?? 10000, filters.page ?? 1, from, to, status)
          // eslint-disable-next-line no-console
          console.log('🔍 Bidder requests full response:', response)
          // eslint-disable-next-line no-console
          console.log('🔍 Bidder requests response.data:', response.data)
          // eslint-disable-next-line no-console
          console.log('🔍 Bidder requests response.data.data:', response.data?.data)
          const raw = response
          data = Array.isArray(raw)
            ? raw
            : Array.isArray(raw?.data)
              ? raw.data
              : Array.isArray(raw?.data?.data)
                ? raw.data.data
                : Array.isArray(raw?.items)
                  ? raw.items
                  : []
          // eslint-disable-next-line no-console
          console.log('🔍 Bidder requests data length:', data.length)
          // eslint-disable-next-line no-console
          console.log('🔍 Bidder requests first item:', data[0])
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('🔍 Bidder requests API error:', error)
          data = []
        }
        filename = `Bidder_Refund_Requests_${new Date().toISOString().split('T')[0]}.csv`
        headers = [
          'Status',
          'Amount (RWF)',
          'Created Date',
          'Transaction Reference',
          'Beneficiary Account Name',
          'Beneficiary Bank Name',
          'Beneficiary Account Number',
          'Phone Number'
        ]
      } else if (activeTab === 'bailiffs') {
        const response = await InstitutionUserService.GetBailiffRefundRequestList(filters.limit ?? 10000, filters.page ?? 1, from, to, status)
        const raw = response
        data = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : Array.isArray(raw?.data?.data)
              ? raw.data.data
              : Array.isArray(raw?.items)
                ? raw.items
                : []
        // eslint-disable-next-line no-console
        console.log('🔍 Bailiff requests response:', response)
        // eslint-disable-next-line no-console
        console.log('🔍 Bailiff requests data length:', data.length)
        filename = `Bailiff_Refund_Requests_${new Date().toISOString().split('T')[0]}.csv`
        headers = [
          'Status',
          'Amount (RWF)',
          'Created Date',
          'Transaction Reference',
          'Beneficiary Account Name',
          'Beneficiary Bank Name',
          'Beneficiary Account Number',
          'Phone Number'
        ]
      } else if (activeTab === 'bid_guarantees') {
        const response = await InstitutionUserService.viewBidGuarantee(filters.limit ?? 10000, filters.page ?? 1, filters.search, filters.from, filters.to)
        data = response.data?.data ?? []
        filename = `Bid_Guarantees_${new Date().toISOString().split('T')[0]}.csv`
        headers = [
          'Status',
          'Amount (RWF)',
          'Created Date',
          'Transaction Reference',
          'Beneficiary Account Name',
          'Beneficiary Bank Name',
          'Beneficiary Account Number',
          'Phone Number'
        ]
      } else if (activeTab === 'payments') {
        const response = await InstitutionUserService.viewDigitalPayments(filters.limit ?? 10000, filters.page ?? 1, filters.search, filters.from, filters.to)
        data = response.data?.data ?? []
        filename = `Payments_${new Date().toISOString().split('T')[0]}.csv`
        headers = [
          'Status',
          'Amount (RWF)',
          'Created Date',
          'Transaction Reference',
          'Beneficiary Account Name',
          'Beneficiary Bank Name',
          'Beneficiary Account Number',
          'Phone Number'
        ]
      } else if (activeTab === 'institutions') {
        const response = await InstitutionUserService.getInstitutionList(filters.limit ?? 10000, filters.page ?? 1, filters.search, filters.from, filters.to)
        data = response.data?.data ?? []
        filename = `Institutions_${new Date().toISOString().split('T')[0]}.csv`
        headers = [
          'Status',
          'Amount (RWF)',
          'Created Date',
          'Transaction Reference',
          'Beneficiary Account Name',
          'Beneficiary Bank Name',
          'Beneficiary Account Number',
          'Phone Number'
        ]
      } else if (activeTab === 'not_requested_bid_security') {
        const search =
          searchText != null && String(searchText).trim() !== '' ? String(searchText).trim() : undefined
        const response: any = await InstitutionUserService.GetBiddersNotRequestedBidSecurityReport({
          limit: 10000,
          page: 1,
          from,
          to,
          search
        })
        data = Array.isArray(response?.data) ? response.data : []
        filename = `No_Bid_Security_Refund_Request_${new Date().toISOString().split('T')[0]}.csv`
        headers = [
          'Reference Number',
          'Transaction Reference',
          'Bidder Full Name',
          'Bidder Phone Number',
          'Bidder Email',
          'Bidder Nid',
          'Property Id',
          'Execution Case No',
          'Payer Code',
          'Payment Amount',
          'Currency',
          'Payment Date Time',
          'Auction Status'
        ]
      }

      if (data.length === 0) {
        // eslint-disable-next-line no-console
        console.log('🔍 No data found with filters:', filters)
        alert(`No data available for download with current filters. Status: ${filterStatus !== '' ? filterStatus : 'All'}, Date Range: ${fromDate !== '' ? fromDate : 'Any'} to ${toDate !== '' ? toDate : 'Any'}`)
        return
      }

      // Create CSV content
      const csvContent = createCSVContent(data, headers)

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = filename
      link.click()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error downloading CSV:', error)
      alert('Error downloading CSV file')
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const downloadFilteredExcel = async (): Promise<void> => {
    try {
      // Get current filters
      const filters = {
        from: fromDate,
        to: toDate,
        status: filterStatus,
        report_type: selectedReportType,
        sort_by: sortBy,
        sort_order: sortOrder,
        limit: 10000, // Get all data without pagination
        page: 1,
        search: ''
      }

      // Fetch data based on active tab
      let data: any[] = []
      let filename = ''
      let headers: string[] = []

      const from = (filters.from != null && filters.from !== '') ? filters.from : undefined
      const to = (filters.to != null && filters.to !== '') ? filters.to : undefined
      const status = (filters.status != null && filters.status !== '') ? filters.status : undefined

      if (activeTab === 'comprehensive') {
        const response = await InstitutionUserService.GetComprehensiveReportData({ ...filters, from, to, status })
        const raw = response
        data = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : Array.isArray(raw?.data?.data)
              ? raw.data.data
              : Array.isArray(raw?.items)
                ? raw.items
                : []
        // Exclude bid and bid_security_payment from downloads
        data = data.filter((item: any) => item.table_source === 'refund_request' || item.table_source === 'bailiff_refund_request')
        filename = `Comprehensive_Report_${new Date().toISOString().split('T')[0]}.xlsx`
        headers = [
          'Status',
          'Amount (RWF)',
          'Created Date',
          'Transaction Reference',
          'Beneficiary Account Name',
          'Beneficiary Bank Name',
          'Beneficiary Account Number',
          'Phone Number'
        ]
      } else if (activeTab === 'bidders') {
        const response = await InstitutionUserService.SearchWithParameters(filters.limit ?? 10000, filters.page ?? 1, from, to, status)
        const raw = response
        data = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : Array.isArray(raw?.data?.data)
              ? raw.data.data
              : Array.isArray(raw?.items)
                ? raw.items
                : []
        // eslint-disable-next-line no-console
        console.log('🔍 Excel - Bidder requests response:', response)
        // eslint-disable-next-line no-console
        console.log('🔍 Excel - Bidder requests data length:', data.length)
        filename = `Bidder_Refund_Requests_${new Date().toISOString().split('T')[0]}.xlsx`
        headers = [
          'Status',
          'Amount (RWF)',
          'Created Date',
          'Transaction Reference',
          'Beneficiary Account Name',
          'Beneficiary Bank Name',
          'Beneficiary Account Number',
          'Phone Number'
        ]
      } else if (activeTab === 'bailiffs') {
        const response = await InstitutionUserService.GetBailiffRefundRequestList(filters.limit ?? 10000, filters.page ?? 1, from, to, status)
        const raw = response
        data = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : Array.isArray(raw?.data?.data)
              ? raw.data.data
              : Array.isArray(raw?.items)
                ? raw.items
                : []
        // eslint-disable-next-line no-console
        console.log('🔍 Excel - Bailiff requests response:', response)
        // eslint-disable-next-line no-console
        console.log('🔍 Excel - Bailiff requests data length:', data.length)
        filename = `Bailiff_Refund_Requests_${new Date().toISOString().split('T')[0]}.xlsx`
        headers = [
          'Status',
          'Amount (RWF)',
          'Created Date',
          'Transaction Reference',
          'Beneficiary Account Name',
          'Beneficiary Bank Name',
          'Beneficiary Account Number',
          'Phone Number'
        ]
      } else if (activeTab === 'bid_guarantees') {
        const response = await InstitutionUserService.viewBidGuarantee(filters.limit ?? 10000, filters.page ?? 1, filters.search, filters.from, filters.to)
        data = response.data?.data ?? []
        filename = `Bid_Guarantees_${new Date().toISOString().split('T')[0]}.xlsx`
        headers = [
          'Status',
          'Amount (RWF)',
          'Created Date',
          'Transaction Reference',
          'Beneficiary Account Name',
          'Beneficiary Bank Name',
          'Beneficiary Account Number',
          'Phone Number'
        ]
      } else if (activeTab === 'payments') {
        const response = await InstitutionUserService.viewDigitalPayments(filters.limit ?? 10000, filters.page ?? 1, filters.search, filters.from, filters.to)
        data = response.data?.data ?? []
        filename = `Payments_${new Date().toISOString().split('T')[0]}.xlsx`
        headers = [
          'Status',
          'Amount (RWF)',
          'Created Date',
          'Transaction Reference',
          'Beneficiary Account Name',
          'Beneficiary Bank Name',
          'Beneficiary Account Number',
          'Phone Number'
        ]
      } else if (activeTab === 'institutions') {
        const response = await InstitutionUserService.getInstitutionList(filters.limit ?? 10000, filters.page ?? 1, filters.search, filters.from, filters.to)
        data = response.data?.data ?? []
        filename = `Institutions_${new Date().toISOString().split('T')[0]}.xlsx`
        headers = [
          'Status',
          'Amount (RWF)',
          'Created Date',
          'Transaction Reference',
          'Beneficiary Account Name',
          'Beneficiary Bank Name',
          'Beneficiary Account Number',
          'Phone Number'
        ]
      } else if (activeTab === 'not_requested_bid_security') {
        const search =
          searchText != null && String(searchText).trim() !== '' ? String(searchText).trim() : undefined
        const response: any = await InstitutionUserService.GetBiddersNotRequestedBidSecurityReport({
          limit: 10000,
          page: 1,
          from,
          to,
          search
        })
        data = Array.isArray(response?.data) ? response.data : []
        filename = `No_Bid_Security_Refund_Request_${new Date().toISOString().split('T')[0]}.xlsx`
        headers = [
          'Reference Number',
          'Transaction Reference',
          'Bidder Full Name',
          'Bidder Phone Number',
          'Bidder Email',
          'Bidder Nid',
          'Property Id',
          'Execution Case No',
          'Payer Code',
          'Payment Amount',
          'Currency',
          'Payment Date Time',
          'Auction Status'
        ]
      }

      if (data.length === 0) {
        // eslint-disable-next-line no-console
        console.log('🔍 No data found with filters:', filters)
        alert(`No data available for download with current filters. Status: ${filterStatus !== '' ? filterStatus : 'All'}, Date Range: ${fromDate !== '' ? fromDate : 'Any'} to ${toDate !== '' ? toDate : 'Any'}`)
        return
      }

      // Create Excel file
      createExcelFile(data, headers, filename)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error downloading Excel:', error)
      alert('Error downloading Excel file')
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const downloadFilteredPDF = async (): Promise<void> => {
    try {
      // Get current filters
      const filters = {
        from: fromDate,
        to: toDate,
        status: filterStatus,
        report_type: selectedReportType,
        sort_by: sortBy,
        sort_order: sortOrder,
        limit: 10000, // Get all data without pagination
        page: 1,
        search: ''
      }

      // Fetch data based on active tab
      let data: any[] = []
      let title = ''

      const from = (filters.from != null && filters.from !== '') ? filters.from : undefined
      const to = (filters.to != null && filters.to !== '') ? filters.to : undefined
      const status = (filters.status != null && filters.status !== '') ? filters.status : undefined

      if (activeTab === 'comprehensive') {
        const response = await InstitutionUserService.GetComprehensiveReportData({ ...filters, from, to, status })
        const raw = response
        data = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : Array.isArray(raw?.data?.data)
              ? raw.data.data
              : Array.isArray(raw?.items)
                ? raw.items
                : []
        // Exclude bid and bid_security_payment from downloads
        data = data.filter((item: any) => item.table_source === 'refund_request' || item.table_source === 'bailiff_refund_request')
        title = 'Comprehensive Report'
      } else if (activeTab === 'bidders') {
        const response = await InstitutionUserService.SearchWithParameters(filters.limit ?? 10000, filters.page ?? 1, from, to, status)
        const raw = response
        data = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : Array.isArray(raw?.data?.data)
              ? raw.data.data
              : Array.isArray(raw?.items)
                ? raw.items
                : []
        // eslint-disable-next-line no-console
        console.log('🔍 PDF - Bidder requests response:', response)
        // eslint-disable-next-line no-console
        console.log('🔍 PDF - Bidder requests data length:', data.length)
        title = 'Bidder Refund Requests'
      } else if (activeTab === 'bailiffs') {
        const response = await InstitutionUserService.GetBailiffRefundRequestList(filters.limit ?? 10000, filters.page ?? 1, from, to, status)
        const raw = response
        data = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : Array.isArray(raw?.data?.data)
              ? raw.data.data
              : Array.isArray(raw?.items)
                ? raw.items
                : []
        // eslint-disable-next-line no-console
        console.log('🔍 PDF - Bailiff requests response:', response)
        // eslint-disable-next-line no-console
        console.log('🔍 PDF - Bailiff requests data length:', data.length)
        title = 'Bailiff Refund Requests'
      } else if (activeTab === 'bid_guarantees') {
        const response = await InstitutionUserService.viewBidGuarantee(filters.limit ?? 10000, filters.page ?? 1, filters.search, filters.from, filters.to)
        data = response.data?.data ?? []
        title = 'Bid Guarantees'
      } else if (activeTab === 'payments') {
        const response = await InstitutionUserService.viewDigitalPayments(filters.limit ?? 10000, filters.page ?? 1, filters.search, filters.from, filters.to)
        data = response.data?.data ?? []
        title = 'Payments'
      } else if (activeTab === 'institutions') {
        const response = await InstitutionUserService.getInstitutionList(filters.limit ?? 10000, filters.page ?? 1, filters.search, filters.from, filters.to)
        data = response.data?.data ?? []
        title = 'Institutions'
      } else if (activeTab === 'not_requested_bid_security') {
        const search =
          searchText != null && String(searchText).trim() !== '' ? String(searchText).trim() : undefined
        const response: any = await InstitutionUserService.GetBiddersNotRequestedBidSecurityReport({
          limit: 10000,
          page: 1,
          from,
          to,
          search
        })
        data = Array.isArray(response?.data) ? response.data : []
        title = 'No bid security refund request'
      }

      if (data.length === 0) {
        // eslint-disable-next-line no-console
        console.log('🔍 No data found with filters:', filters)
        alert(`No data available for download with current filters. Status: ${filterStatus !== '' ? filterStatus : 'All'}, Date Range: ${fromDate !== '' ? fromDate : 'Any'} to ${toDate !== '' ? toDate : 'Any'}`)
        return
      }

      // Create PDF
      if (activeTab === 'not_requested_bid_security') {
        createNotRequestedBidSecurityPdf(data, title)
      } else {
        createPDFFile(data, title)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error downloading PDF:', error)
      alert('Error downloading PDF file')
    }
  }

  // Helper function to create CSV content
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const createCSVContent = (data: any[], headers: string[]): string => {
    // Add filter information header
    const filterInfo = []
    if (fromDate !== '') filterInfo.push(`From Date: ${fromDate}`)
    if (toDate !== '') filterInfo.push(`To Date: ${toDate}`)
    if (filterStatus !== '') filterInfo.push(`Status: ${filterStatus}`)
    if (selectedReportType !== '' && selectedReportType !== 'all') filterInfo.push(`Report Type: ${selectedReportType}`)

    const filterHeader = filterInfo.length > 0 ? `Filters Applied: ${filterInfo.join(', ')}\n\n` : ''

    // Create CSV header row
    const headerRow = headers.join(',')

    // Create data rows
    const dataRows = data.map(item => {
      const row = headers.map(header => {
        let value = ''
        switch (header) {
          case 'Status':
            value = (item.status != null) ? item.status : ''
            break
          case 'Amount (RWF)':
            value = (item.amount != null) ? Number(item.amount).toLocaleString() : ''
            break
          // removed 'Table Source' from exports
          case 'Created Date':
            value = (item.created_at != null) ? new Date(item.created_at).toLocaleDateString() : ''
            break
          case 'Transaction Reference':
            value = (item.transaction_reference != null) ? item.transaction_reference : ''
            break
          case 'Beneficiary Account Name':
            value = (item.beneficiary_account_name != null) ? item.beneficiary_account_name : ''
            break
          case 'Beneficiary Bank Name':
            value = (item.beneficiary_bank_name != null) ? item.beneficiary_bank_name : ''
            break
          case 'Beneficiary Account Number':
            value = (item.beneficiary_account_number != null) ? item.beneficiary_account_number : ''
            break
          case 'Phone Number':
            value = (item.phone_number != null) ? item.phone_number : ''
            break
          default:
            value = (item[header.toLowerCase().replace(/\s+/g, '_')] != null) ? item[header.toLowerCase().replace(/\s+/g, '_')] : ''
        }
        // Escape commas and quotes in CSV
        return `"${String(value).replace(/"/g, '""')}"`
      })
      return row.join(',')
    })

    return filterHeader + headerRow + '\n' + dataRows.join('\n')
  }

  // Helper function to create Excel file
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const createExcelFile = (data: any[], headers: string[], filename: string): void => {
    // Prepare data for Excel
    const excelData = data.map(item => {
      const row: any = {}
      headers.forEach(header => {
        switch (header) {
          case 'Status':
            row[header] = (item.status != null) ? item.status : ''
            break
          case 'Amount (RWF)':
            row[header] = (item.amount != null) ? Number(item.amount) : 0
            break
          case 'Table Source':
            row[header] = (item.table_source != null) ? item.table_source : ''
            break
          case 'Created Date':
            row[header] = (item.created_at != null) ? new Date(item.created_at) : ''
            break
          case 'Transaction Reference':
            row[header] = (item.transaction_reference != null) ? item.transaction_reference : ''
            break
          case 'Beneficiary Account Name':
            row[header] = (item.beneficiary_account_name != null) ? item.beneficiary_account_name : ''
            break
          case 'Beneficiary Bank Name':
            row[header] = (item.beneficiary_bank_name != null) ? item.beneficiary_bank_name : ''
            break
          case 'Beneficiary Account Number':
            row[header] = (item.beneficiary_account_number != null) ? item.beneficiary_account_number : ''
            break
          case 'Phone Number':
            row[header] = (item.phone_number != null) ? item.phone_number : ''
            break
          default:
            row[header] = (item[header.toLowerCase().replace(/\s+/g, '_')] != null) ? item[header.toLowerCase().replace(/\s+/g, '_')] : ''
        }
      })
      return row
    })

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(excelData)

    // Set column widths
    const colWidths = headers.map(() => ({ wch: 20 }))
    ws['!cols'] = colWidths

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Report')

    // Write file
    XLSX.writeFile(wb, filename)
  }

  // Helper function to create PDF file
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const createPDFFile = (data: any[], title: string): void => {
    // eslint-disable-next-line new-cap
    const doc = new jsPDF('landscape')

    // Add title
    doc.setFontSize(16)
    doc.text(title, 20, 20)

    // Add filter information
    doc.setFontSize(10)
    let yPos = 35
    if (fromDate !== '') {
      doc.text(`From Date: ${fromDate}`, 20, yPos)
      yPos += 5
    }
    if (toDate !== '') {
      doc.text(`To Date: ${toDate}`, 20, yPos)
      yPos += 5
    }
    if (filterStatus !== '') {
      doc.text(`Status: ${filterStatus}`, 20, yPos)
      yPos += 5
    }
    if (selectedReportType !== '' && selectedReportType !== 'all') {
      doc.text(`Report Type: ${selectedReportType}`, 20, yPos)
      yPos += 5
    }

    yPos += 10

    // Add table headers (removed Status)
    const headers = ['Amount (RWF)', 'Created Date', 'Transaction Ref', 'Beneficiary Name', 'Bank Name', 'Account Number']
    const colWidths = [30, 25, 35, 40, 35, 30]
    let xPos = 20

    doc.setFontSize(8)
    headers.forEach((header, index) => {
      doc.text(header, xPos, yPos)
      xPos += colWidths[index]
    })

    yPos += 10

    // Add data rows
    data.slice(0, 50).forEach((item, index) => { // Limit to 50 rows for PDF
      if (yPos > 180) {
        doc.addPage()
        yPos = 20
      }

      xPos = 20
      const rowData = [
        (item.amount != null) ? Number(item.amount).toLocaleString() : '',
        (item.created_at != null) ? new Date(item.created_at).toLocaleDateString() : '',
        (item.transaction_reference != null) ? item.transaction_reference : '',
        (item.beneficiary_account_name != null) ? item.beneficiary_account_name : '',
        (item.beneficiary_bank_name != null) ? item.beneficiary_bank_name : '',
        (item.beneficiary_account_number != null) ? item.beneficiary_account_number : ''
      ]

      rowData.forEach((cell, cellIndex) => {
        const cellText = String(cell).length > 20 ? String(cell).substring(0, 17) + '...' : String(cell)
        doc.text(cellText, xPos, yPos)
        xPos += colWidths[cellIndex]
      })

      yPos += 5
    })

    // Save PDF
    doc.save(`${title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`)
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const downloadPDF = (data: DataType[]) => {
    // eslint-disable-next-line new-cap
    const doc = new jsPDF('landscape')

    doc.setFont('Arial', 'normal')
    const marginLeft = 10
    const pageWidth = doc.internal.pageSize.width
    let yPosition = 20

    const logoImagePath = 'assets/img/logo3.png'

    const img = new Image()
    img.src = logoImagePath
    img.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      canvas.width = img.width
      canvas.height = img.height
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      ctx.drawImage(img, 0, 0)
      const logoBase64 = canvas.toDataURL('image/png')

      doc.addImage(logoBase64, 'PNG', marginLeft, 10, 40, 20)
    }

    yPosition += 30

    const currentDate = new Date().toLocaleDateString()
    doc.setFontSize(12)

    doc.text(`REFUND REPORT OF 5 % OF BID SECURITY - ${currentDate}`, marginLeft, yPosition)

    yPosition += 10
    doc.line(marginLeft, yPosition, pageWidth - marginLeft, yPosition)

    yPosition += 5
    yPosition += 5

    doc.setFillColor(0, 123, 255)
    doc.rect(marginLeft - 5, yPosition - 8, pageWidth - 20, 10, 'F')

    doc.setTextColor(255, 255, 255)

    doc.text('No', marginLeft, yPosition)
    doc.text('Beneficiary Name', marginLeft + 20, yPosition)
    doc.text('Bank Name', marginLeft + 70, yPosition)
    doc.text('Account Number', marginLeft + 120, yPosition)
    doc.text('Amount', marginLeft + 190, yPosition)
    doc.text('Date', marginLeft + 235, yPosition)

    doc.setTextColor(0, 0, 0)

    yPosition += 10
    doc.line(marginLeft, yPosition, pageWidth - marginLeft, yPosition)

    data.forEach(item => {
      yPosition += 10
      doc.text(String(item.index), marginLeft, yPosition)
      doc.text(item.beneficiary_account_name, marginLeft + 20, yPosition)
      doc.text(item.beneficiary_bank_name.slice(0, 15), marginLeft + 70, yPosition)
      doc.text(item.beneficiary_account_number, marginLeft + 120, yPosition)
      doc.text(item.amount.toLocaleString(), marginLeft + 190, yPosition)
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      const formatDateTime = (dateString: string | number | Date) => {
        const date = new Date(dateString)
        const day = date.getDate().toString().padStart(2, '0')
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const year = date.getFullYear()
        const hours = date.getHours().toString().padStart(2, '0')
        const minutes = date.getMinutes().toString().padStart(2, '0')

        return `${day}/${month}/${year} ${hours}:${minutes}`
      }

      doc.text(formatDateTime(item.created_at), marginLeft + 235, yPosition)
    })

    // Save the PDF
    doc.save('refunds_report.pdf')
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  /**
   * Export JSPA Data to CSV with Header and Proper Formatting
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const downloadJSPACSV = (data: JSPADataType[]) => {
    // Create a header row with the current date
    const currentDate = new Date().toLocaleDateString()
    const headerTitle = `Bid Security Payment and Refund Report from ${currentDate}`

    // Define column headers
    const columnHeaders = ['Index', 'Transaction Ref', 'Bidder Name', 'Beneficiary Account', 'Bank', 'Amount (RWF)', 'Status', 'Created Date']

    // Map through the data and only pick the predefined columns in the right order
    const filteredData = data.map((item, index) => [
      index + 1, // Adding index starting from 1
      item.transaction_reference,
      item.bidder_full_name,
      item.beneficiary_account_name,
      item.beneficiary_bank_name,
      item.amount !== '' ? parseFloat(item.amount).toLocaleString() : '0', // Format the amount with commas
      item.status,
      item.created_at !== '' ? new Date(item.created_at).toLocaleDateString() : '' // Format the date
    ])

    // Helper function to escape CSV fields properly
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const escapeCSV = (field: any) => {
      if (field == null) return ''
      const str = String(field)
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`
      }
      return str
    }

    // Combine header and data
    const csvContent = [
      headerTitle,
      '', // Empty line
      columnHeaders.map(escapeCSV).join(','),
      ...filteredData.map(row => row.map(escapeCSV).join(','))
    ].join('\n')

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `JSPA_Report_${currentDate.replace(/\//g, '-')}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const downloadJSPAExcelFile = (data: JSPADataType[], filename: string) => {
    // Map through the data and only pick the predefined columns
    const filteredData = data.map((item, index) => ({
      index: index + 1, // Adding index starting from 1
      transaction_reference: item.transaction_reference,
      bidder_full_name: item.bidder_full_name,
      beneficiary_account_name: item.beneficiary_account_name,
      beneficiary_bank_name: item.beneficiary_bank_name,
      amount: item.amount !== '' ? parseFloat(item.amount) : 0,
      status: item.status,
      created_at: item.created_at !== '' ? new Date(item.created_at).toLocaleDateString() : ''
    }))

    // Create a new workbook
    const wb = XLSX.utils.book_new()

    // Create a worksheet from the filtered data
    const ws = XLSX.utils.json_to_sheet(filteredData)

    // Set column widths
    const colWidths = [
      { wch: 8 }, // Index
      { wch: 15 }, // Transaction Ref
      { wch: 25 }, // Bidder Name
      { wch: 25 }, // Beneficiary Account
      { wch: 20 }, // Bank
      { wch: 15 }, // Amount
      { wch: 15 }, // Status
      { wch: 15 } // Created Date
    ]
    ws['!cols'] = colWidths

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'JSPA Report')

    // Write the file to download with styles
    XLSX.writeFile(wb, filename, { bookType: 'xlsx', bookSST: false, type: 'binary' })
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const downloadJSPAPDF = (data: JSPADataType[]) => {
    // eslint-disable-next-line new-cap
    const doc = new jsPDF('landscape')

    doc.setFont('Arial', 'normal')
    const marginLeft = 10
    const pageWidth = doc.internal.pageSize.width
    let yPosition = 20

    // Add title
    doc.setFontSize(16)
    doc.setFont('Arial', 'bold')
    doc.text('Bid Security Payment and Refund Report', pageWidth / 2, yPosition, { align: 'center' })
    yPosition += 15

    // Add date
    doc.setFontSize(10)
    doc.setFont('Arial', 'normal')
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, pageWidth / 2, yPosition, { align: 'center' })
    yPosition += 20

    // Table headers (removed Status)
    const headers = ['No.', 'Txn Ref', 'Bidder', 'Beneficiary', 'Bank', 'Amount', 'Date']
    const colWidths = [8, 12, 25, 25, 20, 15, 15]
    let xPosition = marginLeft

    // Draw headers
    doc.setFontSize(8)
    doc.setFont('Arial', 'bold')
    headers.forEach((header, index) => {
      doc.text(header, xPosition, yPosition)
      xPosition += colWidths[index]
    })
    yPosition += 8

    // Draw data rows
    doc.setFont('Arial', 'normal')
    data.forEach((item, index) => {
      if (yPosition > 180) { // Check if we need a new page
        doc.addPage()
        yPosition = 20
      }

      xPosition = marginLeft
      const rowData = [
        (index + 1).toString(),
        item.transaction_reference,
        item.bidder_full_name,
        item.beneficiary_account_name,
        item.beneficiary_bank_name,
        item.amount !== '' ? parseFloat(item.amount).toLocaleString() : '0',
        item.created_at !== '' ? new Date(item.created_at).toLocaleDateString() : ''
      ]

      rowData.forEach((cell, cellIndex) => {
        doc.text(cell, xPosition, yPosition)
        xPosition += colWidths[cellIndex]
      })
      yPosition += 6
    })

    doc.save('jspa_report.pdf')
  }

  // Comprehensive Report Export Functions
  const downloadComprehensiveCSV = (data: ComprehensiveReportDataType[]): void => {
    const currentDate = new Date().toLocaleDateString()
    const headerTitle = `Comprehensive Report from ${currentDate}`
    const columnHeaders = ['Index', 'Type', 'Transaction Ref', 'Name', 'Account', 'Bank', 'Amount (RWF)', 'Status', 'Created Date']

    const filteredData = data.map((item, index) => [
      index + 1,
      item.table_source.replace('_', ' ').toUpperCase(),
      item.transaction_reference ?? '-',
      item.bidder_full_name ?? item.full_name ?? '-',
      item.beneficiary_account_name ?? '-',
      item.beneficiary_bank_name ?? '-',
      typeof item.amount === 'string' ? parseFloat(item.amount).toLocaleString() : item.amount.toLocaleString(),
      item.status,
      item.created_at !== '' ? new Date(item.created_at).toLocaleDateString() : '-'
    ])

    const csvContent = [
      headerTitle,
      '',
      columnHeaders.join(','),
      ...filteredData.map(row => row.join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `Comprehensive_Report_${currentDate.replace(/\//g, '-')}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const downloadComprehensiveExcel = (data: ComprehensiveReportDataType[], filename: string): void => {
    const filteredData = data.map((item, index) => ({
      index: index + 1,
      type: item.table_source.replace('_', ' ').toUpperCase(),
      transaction_reference: item.transaction_reference ?? '-',
      name: item.bidder_full_name ?? item.full_name ?? '-',
      account: item.beneficiary_account_name ?? '-',
      bank: item.beneficiary_bank_name ?? '-',
      amount: typeof item.amount === 'string' ? parseFloat(item.amount) : item.amount,
      status: item.status,
      created_date: item.created_at !== '' ? new Date(item.created_at).toLocaleDateString() : '-'
    }))

    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(filteredData)
    ws['!cols'] = [
      { wch: 8 }, { wch: 15 }, { wch: 15 }, { wch: 25 },
      { wch: 25 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 15 }
    ]
    XLSX.utils.book_append_sheet(wb, ws, 'Comprehensive Report')
    XLSX.writeFile(wb, filename, { bookType: 'xlsx', bookSST: false, type: 'binary' })
  }

  // Handle tab change
  // Enhanced tab change handler with real-time data fetching
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleTabChange = async (key: string) => {
    // eslint-disable-next-line no-console
    console.log('🔄 Tab changing from', activeTab, 'to', key)

    setActiveTab(key)
    setPagination({ ...pagination, current: 1 }) // Reset to first page when changing tabs

    // Set loading state for the new tab
    setTabLoading(prev => ({ ...prev, [key]: true }))

    try {
      // Check if we need to fetch fresh data (not fetched in last 30 seconds)
      const now = Date.now()
      const lastFetch = lastDataFetch[key] ?? 0
      const shouldFetch = now - lastFetch > 30000 // 30 seconds

      // Get current tab data to check if it's empty
      const currentTabData = key === 'comprehensive'
        ? comprehensiveData
        : key === 'bidders'
          ? biddersData
          : key === 'bailiffs'
            ? bailiffsData
            : key === 'bidSecurityPayments'
              ? bidSecuritiesData
              : key === 'not_requested_bid_security'
                ? notRequestedBidSecurityData
                : []

      if (shouldFetch || currentTabData.length === 0) {
        // eslint-disable-next-line no-console
        console.log('📊 Fetching fresh data for tab:', key)

        const filters = {
          from: fromDate,
          to: toDate,
          status: filterStatus,
          report_type: selectedReportType,
          sort_by: sortBy,
          sort_order: sortOrder
        }

        await fetchDataForActiveTab(filters, key)
        setLastDataFetch(prev => ({ ...prev, [key]: now }))
      } else {
        // eslint-disable-next-line no-console
        console.log('⚡ Using cached data for tab:', key)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching data for tab:', key, error)
    } finally {
      setTabLoading(prev => ({ ...prev, [key]: false }))
    }
  }

  // Refresh all tabs data
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const refreshAllTabs = async () => {
    // eslint-disable-next-line no-console
    console.log('🔄 Refreshing all tabs data...')

    setLoading(true)

    // Set loading state for all tabs
    setTabLoading({
      comprehensive: true,
      bidders: true,
      bailiffs: true,
      bidSecurityPayments: true,
      not_requested_bid_security: true
    })

    try {
      const filters = {
        from: fromDate,
        to: toDate,
        status: filterStatus,
        report_type: selectedReportType,
        sort_by: sortBy,
        sort_order: sortOrder
      }

      // Fetch data for all tabs
      await fetchDataForActiveTab(filters)

      const notReqFilters = { ...filters }
      await fetchDataForActiveTab(notReqFilters, 'not_requested_bid_security')

      // Update last fetch timestamps for all tabs
      const now = Date.now()
      setLastDataFetch({
        comprehensive: now,
        bidders: now,
        bailiffs: now,
        bidSecurityPayments: now,
        not_requested_bid_security: now
      })

      // eslint-disable-next-line no-console
      console.log('All tabs refreshed successfully')
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error refreshing all tabs:', error)
    } finally {
      setLoading(false)
      // Clear all tab loading states
      setTabLoading({
        comprehensive: false,
        bidders: false,
        bailiffs: false,
        bidSecurityPayments: false,
        not_requested_bid_security: false
      })
    }
  }

  // Handle pagination
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleTableChange = (newPagination: any) => {
    setPagination(newPagination)
  }

  // Map data from API response
  const mapResponseData = (responseData: any[], tabType: string): Array<{
    key: any
    index: number
    full_name: any
    phone_number: any
    nid: any
    amount: number
    email: any
    payment_date_time: any
  } | {
    key: any
    index: number
    bank_code: any
    transaction_reference: any
    debit_account_number: any
    debit_account_name: any
    beneficiary_account_name: any
    beneficiary_bank_name: any
    beneficiary_account_number: any
    transaction_currency: any
    amount: number
    reason: any
    status: any
    created_at: any
    updated_at: any
    phone_number: any
    email: any
    link: any
  }> => {
    return responseData.map((item: any, index: number) => {
      if (tabType === 'bidSecurityPayments') {
        return {
          key: item.id ?? index,
          index: index + 1,
          full_name: item.full_name ?? 'N/A',
          phone_number: item.phone_number ?? 'N/A',
          nid: item.nid ?? 'N/A',
          amount: typeof item.amount === 'string' && !isNaN(parseFloat(item.amount))
            ? parseFloat(item.amount)
            : 0,
          email: item.email ?? 'N/A',
          payment_date_time: item.payment_date_time ?? 'N/A'
        }
      }

      return {
        key: item.refund_request_id ?? index,
        index: index + 1,
        bank_code: item.bank_code ?? 'N/A',
        transaction_reference: item.transaction_reference ?? 'N/A',
        debit_account_number: item.debit_account_number ?? 'N/A',
        debit_account_name: item.debit_account_name ?? 'N/A',
        beneficiary_account_name: item.beneficiary_account_name ?? 'N/A',
        beneficiary_bank_name: item.beneficiary_bank_name ?? 'N/A',
        beneficiary_account_number: item.beneficiary_account_number ?? 'N/A',
        transaction_currency: item.transaction_currency ?? 'RWF',
        amount: typeof item.amount === 'string' && !isNaN(parseFloat(item.amount))
          ? parseFloat(item.amount)
          : 0,
        reason: item.reason ?? 'No reason provided',
        status: item.status ?? 'Unknown',
        created_at: item.created_at ?? 'N/A',
        updated_at: item.updated_at ?? 'N/A',
        phone_number: item.phone_number ?? 'N/A',
        email: item.email ?? 'N/A',
        link: item.link ?? 'N/A'
      }
    })
  }

  // Process API response
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const processApiResponse = (res: any, setDataFunction: React.Dispatch<React.SetStateAction<DataType[]>>, tabType: string) => {
    // 'requests' returns response.data directly. Normalize shapes:
    // Possible shapes: Array, { data: Array, meta }, { data }, { items, meta }
    const raw = res
    const list = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw?.data?.data)
          ? raw.data.data
          : Array.isArray(raw?.items)
            ? raw.items
            : []

    const mappedData = mapResponseData(list, tabType)
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    setDataFunction(mappedData)

    const meta = raw?.meta ?? raw?.data?.meta ?? null

    setPagination((prev) => ({
      ...prev,
      total: meta?.total ?? (Array.isArray(list) ? list.length : 0)
    }))

    setCount(meta?.total ?? (Array.isArray(list) ? list.length : 0))

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const getTotalAmount = (value: any) => (isNaN(Number(value)) ? 0 : Number(value))

    setTotals({
      requestedAmount: getTotalAmount(meta?.totals?.requestedAmount),
      refundedAmount: getTotalAmount(meta?.totals?.refundedAmount),
      pendingAmount: getTotalAmount(meta?.totals?.pendingAmount),
      publicTresuryAmount: getTotalAmount(meta?.totals?.publicTresuryAmount)
    })
  }

  // Fetch data based on active tab
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const fetchDataForTab = async (
    // eslint-disable-next-line @typescript-eslint/ban-types
    apiMethod: Function,
    setData: React.Dispatch<React.SetStateAction<any>>,
    filters: FilterParams,
    tabType: string
  ) => {
    try {
      const from = (filters.from != null && filters.from !== '') ? filters.from : undefined
      const to = (filters.to != null && filters.to !== '') ? filters.to : undefined
      const status = (filters.status != null && filters.status !== '') ? filters.status : undefined

      const res = await apiMethod(
        10000, // Large limit to get all data
        1, // Always start from page 1
        from,
        to,
        status
      )
      processApiResponse(res, setData, tabType)
    } catch (error: any) {
      // Ignore CanceledError - it's expected when requests are cancelled due to new requests
      const errorName = typeof error?.name === 'string' ? error.name : ''
      const errorCode = typeof error?.code === 'string' ? error.code : ''
      const errorMessage = typeof error?.message === 'string' ? error.message : ''
      const isCanceledError: boolean = errorName === 'CanceledError' || errorCode === 'ERR_CANCELED' || errorMessage.includes('canceled')
      if (isCanceledError) {
        // eslint-disable-next-line no-console
        console.log('ℹ️ Request canceled (new request started) - this is expected behavior')
        return
      }

      // Only log actual errors, not canceled requests
      // eslint-disable-next-line no-console
      console.error('❌ Error in fetchDataForTab:', error)
      // Handle the error as needed
    }
  }

  const fetchComprehensiveReportData = async (filters: FilterParams, retryCount = 0): Promise<void> => {
    // eslint-disable-next-line no-console
    console.log('🔄 fetchComprehensiveReportData called with filters:', filters)

    // Check if user is authenticated
    const userAuthInfo = localStorage.getItem('userInfo')
    if (userAuthInfo == null || userAuthInfo === '{}') {
      // eslint-disable-next-line no-console
      console.error('🔐 No user authentication found - please login first')
      setLoading(false)
      return
    }
    setLoading(true)

    // Backend returns all rows matching filters (no per-table cap). Optional limit only when no filters.
    const requestedLimit = filters.limit ?? 10000
    const requestLimit = requestedLimit < 1 ? 10000 : requestedLimit
    const requestPage = filters.page ?? pagination.current ?? 1

    try {
      // Sanitize empty filters so backend does not treat empty string as an active filter
      const status = (filters.status != null && filters.status !== '') ? filters.status : undefined

      // Keep date filters optional: when no date range is selected, backend returns paginated latest data.
      const from = (filters.from != null && filters.from !== '') ? filters.from : undefined
      const to = (filters.to != null && filters.to !== '') ? filters.to : undefined

      const reportTypeParam = filters.report_type ?? selectedReportType
      const searchParam = filters.search ?? searchText
      const hasNarrowingFilter =
        (from != null && from !== '') ||
        (to != null && to !== '') ||
        (status != null && status !== '') ||
        String(searchParam ?? '').trim() !== '' ||
        (reportTypeParam != null && reportTypeParam !== '' && reportTypeParam !== 'all')

      const response = await InstitutionUserService.GetAllRefundRequestsCombined({
        ...(hasNarrowingFilter ? { unlimited: '1' } : {}),
        limit: requestLimit,
        page: requestPage,
        search: searchParam,
        status,
        from,
        to,
        sort_by: filters.sort_by ?? sortBy,
        sort_order: filters.sort_order ?? sortOrder,
        request_type: reportTypeParam
      })

      // Some upstream proxies (nginx / load balancer) may return an HTML error page
      // like "<h1>Incomplete response received from application</h1>" instead of JSON.
      // Our http wrapper can pass that through as a "successful" string response, so
      // detect it and convert to an error to trigger the retry/backoff logic below.
      if (typeof response === 'string') {
        const html = response.toLowerCase()
        const looksLikeIncompleteResponse =
          html.includes('incomplete response received from application') ||
          html.includes('<html') ||
          html.includes('<h1')
        if (looksLikeIncompleteResponse) {
          const syntheticError: any = new Error('Incomplete response received from application')
          syntheticError.response = {
            status: 502,
            data: {
              message: 'Incomplete response received from application'
            }
          }
          throw syntheticError
        }
      }

      // Handle response shape differences across environments and wrappers.
      // `requests.get` already returns response.body, while some callers may return axios response.
      const responseDataArray = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : []

      // eslint-disable-next-line no-console
      console.log('✅ Comprehensive report response received:', response?.status)
      // eslint-disable-next-line no-console
      console.log('🔍 Full response structure:', response?.data)
      // eslint-disable-next-line no-console
      console.log('🔍 Is response.data an array?', Array.isArray(response?.data))
      // eslint-disable-next-line no-console
      console.log('🔍 Response data array length:', responseDataArray?.length, 'items')
      // eslint-disable-next-line no-console
      console.log('🔍 First item in response:', responseDataArray?.[0])
      // eslint-disable-next-line no-console
      console.log('🔍 First item table_source:', responseDataArray?.[0]?.table_source)
      // eslint-disable-next-line no-console
      console.log('🔍 First item refund_request_id:', responseDataArray?.[0]?.refund_request_id)
      // eslint-disable-next-line no-console
      console.log('🔍 First item bailiff_refund_request_id:', responseDataArray?.[0]?.bailiff_refund_request_id)
      // eslint-disable-next-line no-console
      console.log('🔍 Meta data in response:', response?.data?.meta)
      // eslint-disable-next-line no-console
      console.log('🔍 Response data keys:', Object.keys(response?.data ?? {}))
      // eslint-disable-next-line no-console
      console.log('🔍 Response data.data type:', typeof response?.data?.data)

      // Handle meta data first, regardless of data array
      // Try different possible locations for meta data
      const metaData = response?.meta ?? null

      if (metaData != null) {
        setComprehensiveMeta(metaData)
        // eslint-disable-next-line no-console
        console.log('✅ Meta data set successfully:', metaData)
        // eslint-disable-next-line no-console
        console.log('💰 Total Amount Refund Requests:', metaData.stats?.totalAmountRefundRequests)

        // Extract and set metadata
        const meta = metaData
        setComprehensiveStats(meta.stats ?? null)
        setStatusBreakdown(meta.status_breakdown ?? {})
        setBankBreakdown(meta.bank_breakdown ?? {})
        setTableSourceBreakdown(meta.table_source_breakdown ?? {})

        // Debug: Log status breakdown data
        // eslint-disable-next-line no-console
        console.log('🔍 Status Breakdown Data:', meta.status_breakdown)
        // eslint-disable-next-line no-console
        console.log('🔍 RECEIVED REQUEST count:', meta.status_breakdown?.['RECEIVED REQUEST'])
        setCount(Number(meta.total ?? 0))

        // Update pagination
        setPagination((prev) => ({
          ...prev,
          total: Number(meta.total ?? 0)
        }))

        // Update filters from meta if available - but skip on initial load to preserve empty filters
        if (meta.filters_applied != null && !isInitialLoad) {
          const appliedFilters = meta.filters_applied
          // eslint-disable-next-line no-console
          console.log('🔧 Applied filters from meta:', appliedFilters)

          // Update filter states if they're different from current values
          if (appliedFilters.from !== fromDate) setFromDate(appliedFilters.from ?? '')
          if (appliedFilters.to !== toDate) setToDate(appliedFilters.to ?? '')
          // Only update status filter if it's not empty and not on initial load
          if (appliedFilters.status !== filterStatus && appliedFilters.status !== '') setFilterStatus(appliedFilters.status ?? '')
          if (appliedFilters.report_type !== selectedReportType) setSelectedReportType(appliedFilters.report_type ?? 'all')
          if (appliedFilters.sort_by !== sortBy) setSortBy(appliedFilters.sort_by ?? 'created_at')
          if (appliedFilters.sort_order !== sortOrder) setSortOrder(appliedFilters.sort_order ?? 'DESC')
        }

        // eslint-disable-next-line no-console
        console.log('✅ Statistics updated:', {
          totalRequests: meta.stats?.totalRequests,
          totalAmount: meta.stats?.totalAmount,
          totalRecords: meta.total,
          pages: meta.pages
        })
      } else {
        // eslint-disable-next-line no-console
        console.warn('⚠️ No meta data found in response')
        setComprehensiveMeta(null)
      }

      if (Array.isArray(responseDataArray) && responseDataArray.length > 0) {
        const processedData: ComprehensiveReportDataType[] = responseDataArray.map((item: any, index: number) => {
          return {
            key: `comprehensive_${String(item.refund_request_id ?? item.bailiff_refund_request_id ?? item.bs_id ?? index)}`,
            index: index + 1,
            // Common fields - ensure table_source defaults correctly
            table_source: item.table_source ?? (
              (item.refund_request_id != null && item.refund_request_id !== '')
                ? 'refund_request'
                : (item.bailiff_refund_request_id != null && item.bailiff_refund_request_id !== '')
                    ? 'bailiff_refund_request'
                    : 'unknown'
            ),
            request_type: item.request_type ?? (
              (item.refund_request_id != null && item.refund_request_id !== '')
                ? 'refund'
                : (item.bailiff_refund_request_id != null && item.bailiff_refund_request_id !== '')
                    ? 'bailiff'
                    : 'unknown'
            ),
            status: item.status ?? 'UNKNOWN',
            amount: item.amount ?? '0',
            created_at: item.created_at ?? '',
            updated_at: item.updated_at ?? '',

            // Refund Request fields
            refund_request_id: item.refund_request_id ?? '',
            bailiff_refund_request_id: item.bailiff_refund_request_id ?? '',
            bank_code: item.bank_code ?? '',
            transaction_reference: item.transaction_reference ?? '',
            debit_account_number: item.debit_account_number ?? '',
            debit_account_name: item.debit_account_name ?? '',
            beneficiary_account_name: item.beneficiary_account_name ?? '',
            beneficiary_bank_name: item.beneficiary_bank_name ?? '',
            beneficiary_account_number: item.beneficiary_account_number ?? '',
            transaction_currency: item.transaction_currency ?? 'RWF',
            reason: item.reason ?? '',
            phone_number: item.phone_number ?? '',
            email: item.email ?? '',

            // Bailiff-specific fields
            bailiff_assignee: item.bailiff_assignee ?? '',
            bailiff_national_id: item.bailiff_national_id ?? '',
            bailiff_email: item.bailiff_email ?? '',
            bailiff_phone_number: item.bailiff_phone_number ?? '',
            property_id: item.property_id ?? '',
            execution_case_number: item.execution_case_number ?? '',
            attachment: item.attachment ?? '',
            created_by: item.created_by ?? '',
            otp: item.otp ?? '',
            otp_expiration: item.otp_expiration ?? '',

            // Bidder Info
            bidder_id: item.bidder_id ?? '',
            bidder_full_name: item.bidder_full_name ?? '',
            bidder_nid: item.bidder_nid ?? '',
            bidder_phone_number: item.bidder_phone_number ?? '',
            bidder_email: item.bidder_email ?? '',

            // Bid Security Info
            bid_security_id: item.bid_security_id ?? '',
            bid_security_ref_number: item.bid_security_ref_number ?? '',
            bid_security_type: item.bid_security_type ?? '',
            bid_security_amount_covered: item.bid_security_amount_covered ?? '',
            bid_security_payment_id: item.bid_security_payment_id ?? '',
            bid_security_created_at: item.bid_security_created_at ?? '',

            // Bid Security Payment fields
            bs_id: item.bs_id ?? '',
            ref_number: item.ref_number ?? '',
            type: item.type ?? '',
            amount_covered: item.amount_covered ?? '',
            bs_expiration_date: item.bs_expiration_date ?? '',
            used: Boolean(item.used),
            payment_id: item.payment_id ?? '',
            currency: item.currency ?? '',
            payment_date_time: item.payment_date_time ?? '',
            payer_code: item.payer_code ?? '',
            payer_name: item.payer_name ?? '',
            payment_channel: item.payment_channel ?? '',
            full_name: item.full_name ?? '',
            nid: item.nid ?? ''
          }
        })

        // eslint-disable-next-line no-console
        console.log('📊 Processed comprehensive data:', processedData.length, 'items')
        // eslint-disable-next-line no-console
        console.log('📋 Sample processed item:', processedData[0])
        // eslint-disable-next-line no-console
        console.log('📋 Sample item table_source:', processedData[0]?.table_source)
        // eslint-disable-next-line no-console
        console.log('📋 Unique table_source values:', Array.from(new Set(processedData.map(item => item.table_source))))

        // Set the data
        setComprehensiveData(processedData)

        // eslint-disable-next-line no-console
        console.log('✅ Comprehensive data set successfully - Data should now have', processedData.length, 'items')
      } else {
        // eslint-disable-next-line no-console
        console.log('⚠️ No comprehensive data received or empty response - keeping existing data')
        setComprehensiveData([])
        // Note: Meta data is already handled above, regardless of data array
      }
    } catch (error: any) {
      // Ignore CanceledError - it's expected when requests are cancelled due to new requests
      const errorName = typeof error?.name === 'string' ? error.name : ''
      const errorCode = typeof error?.code === 'string' ? error.code : ''
      const errorMessage = typeof error?.message === 'string' ? error.message : ''
      const isCanceledError: boolean = errorName === 'CanceledError' || errorCode === 'ERR_CANCELED' || errorMessage.includes('canceled')
      if (isCanceledError) {
        // eslint-disable-next-line no-console
        console.log('ℹ️ Request canceled (new request started) - this is expected behavior')
        return
      }

      // Only log actual errors, not canceled requests
      // eslint-disable-next-line no-console
      console.error('❌ Error fetching comprehensive report data:', error)
      // eslint-disable-next-line no-console
      console.error('❌ Error response:', error?.response?.data)
      // eslint-disable-next-line no-console
      console.error('❌ Error status:', error?.response?.status)

      const errorStatus = error?.response?.status

      // Handle timeout errors (408 Request Timeout)
      if (errorStatus === 408 || Boolean(errorMessage?.includes('timeout')) || Boolean(errorMessage?.includes('ETIMEDOUT'))) {
        const maxRetries = 3
        if (retryCount < maxRetries) {
          // eslint-disable-next-line no-console
          console.log(`🔄 Timeout error - Retrying with reduced limit... (Attempt ${retryCount + 1}/${maxRetries})`)
          // Wait before retrying (exponential backoff)
          const delay = Math.min(1000 * Math.pow(2, retryCount), 5000)
          await new Promise(resolve => setTimeout(resolve, delay))
          // Retry with more aggressive limit reduction
          const reducedLimit = Math.max(100, Math.floor(requestLimit / 2))
          void message.warning(`Request timeout. Retrying with reduced limit (${reducedLimit})...`)
          await fetchComprehensiveReportData({ ...filters, limit: reducedLimit }, retryCount + 1)
          return
        } else {
          void message.error('Request timeout: The query took too long. Please reduce the date range, add filters, or use pagination.')
          setLoading(false)
          return
        }
      }

      // Handle 502 Bad Gateway errors with retry logic
      if (errorStatus === 502 || Boolean(errorMessage?.includes('Incomplete response')) || Boolean(errorMessage?.includes('incomplete'))) {
        const maxRetries = 3
        if (retryCount < maxRetries) {
          // eslint-disable-next-line no-console
          console.log(`🔄 Incomplete response error - Retrying with reduced limit... (Attempt ${retryCount + 1}/${maxRetries})`)
          // Wait before retrying (exponential backoff)
          const delay = Math.min(1000 * Math.pow(2, retryCount), 5000)
          await new Promise(resolve => setTimeout(resolve, delay))
          // Retry with more aggressive limit reduction: 50% reduction each time
          const reducedLimit = Math.max(100, Math.floor(requestLimit / 2))
          void message.warning(`Server timeout detected. Retrying with reduced limit (${reducedLimit})...`)
          await fetchComprehensiveReportData({ ...filters, limit: reducedLimit }, retryCount + 1)
          return
        } else {
          void message.error('Server timeout: Unable to load all data. Please reduce the date range, add filters, or use pagination to access data in smaller chunks.')
          // eslint-disable-next-line no-console
          console.error('❌ Max retries reached for incomplete response error')
        }
      } else if (errorStatus === 401) {
        // eslint-disable-next-line no-console
        console.error('🔐 Authentication failed - user may need to login')
        void message.error('Authentication failed. Please login again.')
      } else if (errorStatus === 500) {
        void message.error('Server error occurred. Please try again later or contact support.')
      } else if (errorStatus != null) {
        void message.error(`Failed to load data. Error: ${String(errorStatus)}. Please try again or contact support.`)
      } else {
        void message.error('Network error: Unable to connect to the server. Please check your internet connection and try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const fetchRefundData = async (filters: FilterParams) => {
    // eslint-disable-next-line no-console
    console.log('🔄 fetchRefundData called with filters:', filters)

    // Check if user is authenticated
    const userAuthInfo = localStorage.getItem('userInfo')
    if (userAuthInfo == null || userAuthInfo === '{}') {
      // eslint-disable-next-line no-console
      console.error('🔐 No user authentication found - please login first')
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const response = await InstitutionUserService.GetAllRefundRequestsCombined({
        from: filters.from,
        to: filters.to,
        status: filters.status,
        limit: '1000',
        page: '1'
      })

      // eslint-disable-next-line no-console
      console.log('✅ Raw response received:', response?.status, response?.data?.data?.length, 'items')

      if (response?.data?.data != null && Array.isArray(response.data.data) && response.data.data.length > 0) {
        const processedData: JSPADataType[] = response.data.data.map((item: any, index: number) => {
          // Determine the ID based on request type and table source
          const recordId = item.bailiff_refund_request_id ?? item.refund_request_id ?? `record_${index}`
          const tableSource = item.table_source ?? (item.bailiff_refund_request_id != null ? 'bailiff_refund_request' : 'refund_request')
          // const requestType = item.request_type ?? (item.bailiff_refund_request_id != null ? 'bailiff' : 'refund')
          return {
            key: `refund_${String(recordId)}`,
            index: index + 1,
            // Main refund request info
            bailiff_refund_request_id: recordId,
            bank_code: item.bank_code ?? '',
            transaction_reference: item.transaction_reference ?? '',
            debit_account_number: item.debit_account_number ?? '',
            debit_account_name: item.debit_account_name ?? '',
            beneficiary_account_name: item.beneficiary_account_name ?? '',
            beneficiary_bank_name: item.beneficiary_bank_name ?? '',
            beneficiary_account_number: item.beneficiary_account_number ?? '',
            amount: item.amount?.toString() ?? '0',
            transaction_currency: item.transaction_currency ?? 'RWF',
            reason: item.reason ?? '',
            // Bailiff-specific fields (only for bailiff requests)
            bailiff_assignee: item.bailiff_assignee ?? '',
            bailiff_national_id: item.bailiff_national_id ?? '',
            bailiff_email: item.bailiff_email ?? '',
            bailiff_phone_number: item.bailiff_phone_number ?? '',
            property_id: item.property_id ?? '',
            execution_case_number: item.execution_case_number ?? '',
            attachment: item.attachment ?? '',
            // Common fields
            status: item.status ?? 'UNKNOWN',
            created_by: item.created_by ?? '',
            otp: item.otp ?? '',
            otp_expiration: item.otp_expiration ?? '',
            created_at: item.created_at ?? '',
            updated_at: item.updated_at ?? '',
            // Bidder Info - extract from nested bidders object or fallback to direct fields
            bidder_id: item.bidder_id ?? '',
            bidder_full_name: item.bidders?.full_name ?? item.beneficiary_account_name ?? '',
            bidder_nid: item.bidders?.nid ?? '',
            bidder_phone_number: item.bidders?.phone_number ?? item.phone_number ?? item.bailiff_phone_number ?? '',
            bidder_email: item.bidders?.email ?? item.email ?? item.bailiff_email ?? '',
            // Bid Security Info - extract from nested bid_securities object
            bid_security_id: item.bid_security_id ?? '',
            bid_security_ref_number: item.bid_securities?.ref_number ?? '',
            bid_security_type: item.bid_securities?.type ?? '',
            bid_security_amount_covered: item.bid_securities?.amount_covered?.toString() ?? '0',
            bid_security_payment_id: item.bid_securities?.payment_id ?? '',
            bid_security_created_at: item.bid_securities?.created_at ?? '',
            // Store table source for dynamic column generation
            table_source: tableSource
          }
        })

        // eslint-disable-next-line no-console
        console.log('📊 Processed data:', processedData.length, 'items')
        // eslint-disable-next-line no-console
        console.log('📋 Sample processed item:', processedData[0])
        // eslint-disable-next-line no-console
        console.log('🏷️ Table sources found:', Array.from(new Set(processedData.map(item => item.table_source))))

        // Set the data
        setJspaData(processedData)
        setFilteredJspaData(processedData)
        calculateJSPAStats(processedData)
        calculateJSPASummary(processedData)

        // eslint-disable-next-line no-console
        console.log('✅ Data set successfully - Data should now have', processedData.length, 'items')
        // eslint-disable-next-line no-console
        console.log('🔍 Current Data state after setting:', processedData)
      } else {
        // eslint-disable-next-line no-console
        console.log('⚠️ No data received or empty response - keeping existing data')
      }
    } catch (error: any) {
      // Ignore CanceledError - it's expected when requests are cancelled due to new requests
      const errorName = typeof error?.name === 'string' ? error.name : ''
      const errorCode = typeof error?.code === 'string' ? error.code : ''
      const errorMessage = typeof error?.message === 'string' ? error.message : ''
      const isCanceledError: boolean = errorName === 'CanceledError' || errorCode === 'ERR_CANCELED' || errorMessage.includes('canceled')
      if (isCanceledError) {
        // eslint-disable-next-line no-console
        console.log('ℹ️ Request canceled (new request started) - this is expected behavior')
        return
      }

      // Only log actual errors, not canceled requests
      // eslint-disable-next-line no-console
      console.error('❌ Error fetching refund data:', error)
      // eslint-disable-next-line no-console
      console.error('❌ Error response:', error?.response?.data)
      // eslint-disable-next-line no-console
      console.error('❌ Error status:', error?.response?.status)

      if (error?.response?.status === 401) {
        // eslint-disable-next-line no-console
        console.error('🔐 Authentication failed - user may need to login')
      }
    } finally {
      setLoading(false)
    }
  }

  // Enhanced JSPA processing functions
  const calculateJSPAStats = (data: JSPADataType[]): void => {
    const stats: JSPAStats = {
      totalRequests: data.length,
      totalAmount: data.reduce((sum, item) => sum + parseFloat(item.amount !== '' ? item.amount : '0'), 0),
      pendingRequests: data.filter(item => item.status === 'PENDING').length,
      approvedRequests: data.filter(item => item.status === 'APPROVED' || item.status === 'COMPLETED').length,
      rejectedRequests: data.filter(item => item.status === 'REJECTED').length,
      receivedRequests: data.filter(item => item.status === 'RECEIVED REQUEST').length,
      averageAmount: 0,
      completionRate: 0
    }

    stats.averageAmount = stats.totalRequests > 0 ? stats.totalAmount / stats.totalRequests : 0
    stats.completionRate = stats.totalRequests > 0
      ? ((stats.approvedRequests + stats.rejectedRequests) / stats.totalRequests) * 100
      : 0

    setJspaStats(stats)
  }

  const calculateJSPASummary = (data: JSPADataType[]): void => {
    const summary: JSPASummary = {
      byStatus: {},
      byBank: {},
      byTableSource: {},
      monthlyTrend: []
    }

    // Calculate by status
    data.forEach(item => {
      summary.byStatus[item.status] = (summary.byStatus[item.status] ?? 0) + 1
    })

    // Calculate by bank
    data.forEach(item => {
      const bank = item.beneficiary_bank_name !== '' ? item.beneficiary_bank_name : 'Unknown'
      summary.byBank[bank] = (summary.byBank[bank] ?? 0) + 1
    })

    // Calculate by table source
    data.forEach(item => {
      summary.byTableSource[item.table_source] = (summary.byTableSource[item.table_source] ?? 0) + 1
    })

    // Calculate monthly trend
    const monthlyData: Record<string, { count: number, amount: number }> = {}
    data.forEach(item => {
      const month = new Date(item.created_at).toISOString().substring(0, 7) // YYYY-MM
      if (monthlyData[month] == null) {
        monthlyData[month] = { count: 0, amount: 0 }
      }
      monthlyData[month].count++
      monthlyData[month].amount += parseFloat(item.amount !== '' ? item.amount : '0')
    })

    summary.monthlyTrend = Object.entries(monthlyData)
      .map(([month, trendData]) => ({ month, ...trendData }))
      .sort((a, b) => a.month.localeCompare(b.month))

    setJspaSummary(summary)
  }

  // Enhanced filtering functions
  const applyJSPAFilters = (): void => {
    let filtered = [...jspaData]

    // Search filter
    if (searchText !== '') {
      filtered = filtered.filter(item =>
        item.transaction_reference.toLowerCase().includes(searchText.toLowerCase()) ||
        item.bidder_full_name.toLowerCase().includes(searchText.toLowerCase()) ||
        item.beneficiary_account_name.toLowerCase().includes(searchText.toLowerCase()) ||
        item.beneficiary_bank_name.toLowerCase().includes(searchText.toLowerCase())
      )
    }

    // Status filter
    if (selectedStatus !== '') {
      filtered = filtered.filter(item => item.status === selectedStatus)
    }

    // Bank filter
    if (selectedBank !== '') {
      filtered = filtered.filter(item => item.beneficiary_bank_name === selectedBank)
    }

    // Table source filter
    if (selectedTableSource !== '') {
      filtered = filtered.filter(item => item.table_source === selectedTableSource)
    }

    setFilteredJspaData(filtered)
  }

  // Memoized filtered data
  const memoizedFilteredData = useMemo(() => {
    return filteredJspaData
  }, [filteredJspaData])

  // Get unique values for filter options
  const uniqueBanks = useMemo(() => {
    if (activeTab === 'comprehensive' && Object.keys(bankBreakdown).length > 0) {
      return Object.keys(bankBreakdown)
    }
    return Array.from(new Set(jspaData.map(item => item.beneficiary_bank_name).filter(Boolean)))
  }, [jspaData, bankBreakdown, activeTab])

  const uniqueStatuses = useMemo(() => {
    if (activeTab === 'comprehensive' && Object.keys(statusBreakdown).length > 0) {
      return Object.keys(statusBreakdown)
    }
    return Array.from(new Set(jspaData.map(item => item.status).filter(Boolean)))
  }, [jspaData, statusBreakdown, activeTab])

  const uniqueTableSources = useMemo(() => {
    if (activeTab === 'comprehensive' && Object.keys(tableSourceBreakdown).length > 0) {
      return Object.keys(tableSourceBreakdown)
    }
    return Array.from(new Set(jspaData.map(item => item.table_source).filter(Boolean)))
  }, [jspaData, tableSourceBreakdown, activeTab])

  // Filter comprehensive data based on selected data source tab
  // Note: For the comprehensive tab, we show only bailiff_refund_request and refund_request data combined
  const filteredComprehensiveData = useMemo(() => {
    if (selectedDataSourceTab === 'all' || activeTab === 'comprehensive') {
      // For comprehensive tab, filter to show only bailiff_refund_request and refund_request data
      if (activeTab === 'comprehensive') {
        return comprehensiveData.filter(item =>
          item.table_source === 'bailiff_refund_request' || item.table_source === 'refund_request'
        )
      }
      return comprehensiveData
    }
    return comprehensiveData.filter(item => item.table_source === selectedDataSourceTab)
  }, [comprehensiveData, selectedDataSourceTab, activeTab])

  const fetchDataForActiveTab = async (filters: FilterParams, tabKeyOverride?: string): Promise<void> => {
    const effectiveTab = tabKeyOverride ?? activeTab
    // eslint-disable-next-line no-console
    console.log('🔄 fetchDataForActiveTab called with activeTab:', effectiveTab, 'filters:', filters)

    if (effectiveTab === 'not_requested_bid_security') {
      const userAuthInfo = localStorage.getItem('userInfo')
      if (userAuthInfo == null || userAuthInfo === '{}') {
        // eslint-disable-next-line no-console
        console.error('🔐 No user authentication found - please login first')
        return
      }
      setLoading(true)
      try {
        const from =
          filters.from != null && filters.from !== '' ? filters.from : undefined
        const to =
          filters.to != null && filters.to !== '' ? filters.to : undefined
        const searchParam = filters.search ?? searchText

        const response: any = await InstitutionUserService.GetBiddersNotRequestedBidSecurityReport({
          limit: 10000,
          page: 1,
          search: String(searchParam ?? '').trim() !== '' ? String(searchParam).trim() : undefined,
          from,
          to
        })

        const rawRows: any[] = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : []

        const mapped: NotRequestedBidSecurityRow[] = rawRows.map((item: any, index: number) => ({
          ...item,
          key: `${String(item.bid_security_ref_number ?? '')}-${String(item.bidder_id ?? '')}-${String(item.payment_id ?? index)}`,
          index: index + 1
        }))
        setNotRequestedBidSecurityData(mapped)
      } catch (error: any) {
        const errorName = typeof error?.name === 'string' ? error.name : ''
        const errorCode = typeof error?.code === 'string' ? error.code : ''
        const errorMessage = typeof error?.message === 'string' ? error.message : ''
        const isCanceledError: boolean =
          errorName === 'CanceledError' || errorCode === 'ERR_CANCELED' || errorMessage.includes('canceled')
        if (isCanceledError) {
          return
        }
        // eslint-disable-next-line no-console
        console.error('❌ Error fetching not-requested bid security report:', error)
        void message.error('Failed to load bidders who have not requested bid security refund.')
        setNotRequestedBidSecurityData([])
      } finally {
        setLoading(false)
      }
      return
    }

    setLoading(true)
    try {
      // Only fetch comprehensive data - all tabs will filter from this single source
      // eslint-disable-next-line no-console
      console.log('📊 Fetching Comprehensive Report data (single source for all tabs)...')
      await fetchComprehensiveReportData(filters)

      // No need to fetch separate APIs - we'll filter comprehensiveData for each tab
      // eslint-disable-next-line no-console
      console.log('✅ Comprehensive data loaded - tabs will filter from this data')
    } catch (error: any) {
      // Ignore CanceledError - it's expected when requests are cancelled due to new requests
      const errorName = typeof error?.name === 'string' ? error.name : ''
      const errorCode = typeof error?.code === 'string' ? error.code : ''
      const errorMessage = typeof error?.message === 'string' ? error.message : ''
      const isCanceledError: boolean = errorName === 'CanceledError' || errorCode === 'ERR_CANCELED' || errorMessage.includes('canceled')
      if (isCanceledError) {
        // eslint-disable-next-line no-console
        console.log('ℹ️ Request canceled (new request started) - this is expected behavior')
        return
      }

      // Only log actual errors, not canceled requests
      // eslint-disable-next-line no-console
      console.error('❌ Error in fetchDataForActiveTab:', error)
    } finally {
      setLoading(false)
    }
  }

  // Initial mount effect - load all data with no filters when page first loads
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('🚀 Initial load - fetching all data with no filters...')

    // Explicitly set to show all data with no filters on initial mount
    const initialFilters = {
      from: '',
      to: '',
      status: '',
      report_type: 'all',
      sort_by: 'created_at',
      sort_order: 'DESC'
    }

    // Ensure filters are empty before initial fetch
    setFromDate('')
    setToDate('')
    setFilterStatus('')
    setSelectedReportType('all')

    // Reset form to ensure no dates are set initially
    form.setFieldsValue({
      range_picker: null,
      filterStatus: undefined,
      reportType: 'all'
    })

    // Fetch comprehensive data, then prefetch "not requested bid security" so that tab has rows on first open
    void fetchDataForActiveTab(initialFilters).then(() => {
      setIsInitialLoad(false)
      void fetchDataForActiveTab(initialFilters, 'not_requested_bid_security')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty dependency array - only run on mount

  // Existing useEffect for filter/state changes after initial load
  useEffect(() => {
    // Skip if this is the initial load (handled by the mount effect)
    if (isInitialLoad) {
      return
    }

    // eslint-disable-next-line no-console
    console.log('🚀 Loading data with filters...')

    const filters = {
      from: fromDate,
      to: toDate,
      status: filterStatus,
      report_type: selectedReportType,
      sort_by: sortBy,
      sort_order: sortOrder
    }

    // Use fetchDataForActiveTab which handles all data fetching including comprehensive data
    void fetchDataForActiveTab(filters)
  }, [fromDate, toDate, filterStatus, activeTab, selectedReportType, sortBy, sortOrder, isInitialLoad])

  // Auto-refresh every 60 seconds for all page data/statistics
  useEffect(() => {
    const interval = setInterval(() => {
      const filters = {
        from: fromDate,
        to: toDate,
        status: filterStatus,
        report_type: selectedReportType,
        sort_by: sortBy,
        sort_order: sortOrder
      }
      void fetchDataForActiveTab(filters)
    }, 60000) // 60000 ms = 60 seconds
    return () => { clearInterval(interval) } // use braces for cleanup arrow
  }, [fromDate, toDate, filterStatus, activeTab, selectedReportType, sortBy, sortOrder])

  // Update form values when meta data is loaded - but skip on initial load to preserve empty filters
  useEffect(() => {
    // Skip updating form from meta on initial load to prevent applying backend default filters
    if (isInitialLoad) {
      return
    }

    if (comprehensiveMeta?.filters_applied != null) {
      const appliedFilters = comprehensiveMeta.filters_applied

      // Update form values only if status filter is not empty (to avoid applying default "RECEIVED REQUEST")
      if (appliedFilters.status !== '' && appliedFilters.status != null) {
        form.setFieldsValue({
          filterStatus: appliedFilters.status,
          reportType: appliedFilters.report_type ?? 'all'
        })
      } else {
        // Only update reportType if status is empty
        form.setFieldsValue({
          reportType: appliedFilters.report_type ?? 'all'
        })
      }

      // Don't update date range from meta - keep it empty by default
      // Date range should be explicitly set by user, not from backend meta

      // eslint-disable-next-line no-console
      console.log('📝 Form values updated from meta:', {
        filterStatus: appliedFilters.status,
        reportType: appliedFilters.report_type,
        dateRange: 'No date range (user must set explicitly)'
      })
    }
  }, [comprehensiveMeta, form, isInitialLoad])

  // Filter handlers
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const clearFilters = () => {
    form.resetFields()
    form.setFieldsValue({
      range_picker: null,
      filterStatus: undefined,
      reportType: 'all',
      search: ''
    })
    setFromDate('')
    setToDate('')
    setFilterStatus('')
    setSelectedReportType('all')
    setSearchText('')
    setPagination({ ...pagination, current: 1 })
    void fetchDataForActiveTab({ from: '', to: '', status: '', report_type: 'all', search: '' })
  }

  const handleFilterSearch = (values: any): void => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const { range_picker, filterStatus: formStatus, reportType, search: formSearch } = values

    let from = ''
    let to = ''

    if (Array.isArray(range_picker) && range_picker.length === 2) {
      from = range_picker[0].format('YYYY-MM-DD')
      to = range_picker[1].format('YYYY-MM-DD')
    }

    const resolvedStatus = formStatus ?? filterStatus ?? ''
    const resolvedSearch = formSearch ?? searchText ?? ''

    setFromDate(from)
    setToDate(to)
    setFilterStatus(resolvedStatus)
    setSelectedReportType(reportType ?? 'all')
    setSearchText(resolvedSearch)

    setPagination({ ...pagination, current: 1 })
    void fetchDataForActiveTab({
      from,
      to,
      status: resolvedStatus,
      report_type: reportType ?? 'all',
      sort_by: sortBy,
      sort_order: sortOrder,
      search: resolvedSearch
    })
  }

  // Get current data based on active tab
  // const getCurrentData = (): DataType[] => {
  //   return activeTab === 'bidders' ? biddersData : bailiffsData
  // }

  const getCurrentData = (): DataType[] => {
    switch (activeTab) {
      case 'refund':
        return [...biddersData, ...bailiffsData]
      case 'bidSecurityPayments':
        return bidSecuritiesData
      default:
        return []
    }
  }

  const getCurrentJSPAData = (): JSPADataType[] => {
    const data = activeTab === 'jspa' ? memoizedFilteredData : []
    // eslint-disable-next-line no-console
    console.log('📋 getCurrentJSPAData - activeTab:', activeTab, 'data length:', data.length)
    return data
  }

  const getCurrentComprehensiveData = (): ComprehensiveReportDataType[] => {
    if (activeTab === 'comprehensive') {
      // Filter to show only bailiff_refund_request and refund_request data
      const filteredData = comprehensiveData.filter(item =>
        item.table_source === 'bailiff_refund_request' || item.table_source === 'refund_request'
      )
      // eslint-disable-next-line no-console
      console.log('📋 getCurrentComprehensiveData - activeTab:', activeTab, 'filtered data length:', filteredData.length)
      return filteredData
    }
    return []
  }

  // Effect hooks for filtering
  useEffect(() => {
    applyJSPAFilters()
  }, [searchText, selectedStatus, selectedBank, selectedTableSource, jspaData])

  // Effect hook to recalculate stats when filtered data changes
  useEffect(() => {
    if (filteredJspaData.length > 0) {
      calculateJSPAStats(filteredJspaData)
    }
  }, [filteredJspaData])

  // Column definitions

  // Dynamic column generation based on table_source
  const generateDynamicColumns = (data: JSPADataType[]): ColumnsType<JSPADataType> => {
    // Get unique table sources to determine column structure
    const tableSources = Array.from(new Set(data.map(item => item.table_source)))
    const hasBailiffData = data.some(item => item.table_source === 'bailiff_refund_request')
    const hasBidderData = data.some(item => item.table_source === 'refund_request')

    // eslint-disable-next-line no-console
    console.log('🔧 Generating dynamic columns for:', data.length, 'items')
    // eslint-disable-next-line no-console
    console.log('📊 Table sources found:', tableSources)
    // eslint-disable-next-line no-console
    console.log('🏷️ Has bailiff data:', hasBailiffData, 'Has bidder data:', hasBidderData)

    const baseColumns: ColumnsType<JSPADataType> = [
      {
        title: 'No.',
        dataIndex: 'index',
        key: 'index',
        width: 60,
        sorter: (a: any, b: any) => a.index - b.index,
        ellipsis: true,
        fixed: 'left'
      },
      {
        title: 'Transaction Ref',
        dataIndex: 'transaction_reference',
        key: 'transaction_reference',
        width: 120,
        sorter: (a: any, b: any) => a.transaction_reference.localeCompare(b.transaction_reference, 'en', { numeric: true }),
        ellipsis: true,
        render: (value: string) => (
          <Tooltip title={value}>
            <span style={{ fontFamily: 'inherit', fontWeight: 600 }}>{value}</span>
          </Tooltip>
        )
      }
    ]

    // Add conditional columns based on data availability
    const conditionalColumns: ColumnsType<JSPADataType> = []

    // Bidder-specific columns
    if (hasBidderData) {
      conditionalColumns.push({
        title: 'Bidder Name',
        dataIndex: 'bidder_full_name',
        key: 'bidder_full_name',
        width: 200,
        sorter: (a: any, b: any) => a.bidder_full_name.localeCompare(b.bidder_full_name, 'en', { numeric: true }),
        ellipsis: true,
        render: (value: string) => (
          <Tooltip title={value}>
            <span style={{ fontWeight: '500', fontSize: '12px' }}>{value}</span>
          </Tooltip>
        )
      })
    }

    // Bailiff-specific columns
    if (hasBailiffData) {
      conditionalColumns.push(
        {
          title: 'Bailiff Assignee',
          dataIndex: 'bailiff_assignee',
          key: 'bailiff_assignee',
          width: 180,
          sorter: (a: any, b: any) => a.bailiff_assignee.localeCompare(b.bailiff_assignee, 'en', { numeric: true }),
          ellipsis: true,
          render: (value: string) => value !== '' ? value : '-'
        },
        {
          title: 'Property ID',
          dataIndex: 'property_id',
          key: 'property_id',
          width: 120,
          sorter: (a: any, b: any) => a.property_id.localeCompare(b.property_id, 'en', { numeric: true }),
          ellipsis: true,
          render: (value: string) => value !== '' ? value : '-'
        },
        {
          title: 'Case Number',
          dataIndex: 'execution_case_number',
          key: 'execution_case_number',
          width: 150,
          sorter: (a: any, b: any) => a.execution_case_number.localeCompare(b.execution_case_number, 'en', { numeric: true }),
          ellipsis: true,
          render: (value: string) => value !== '' ? value : '-'
        }
      )
    }

    // Common columns that apply to both types
    const commonColumns: ColumnsType<JSPADataType> = [
      {
        title: 'Beneficiary Account',
        dataIndex: 'beneficiary_account_name',
        key: 'beneficiary_account_name',
        width: 140,
        responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        sorter: (a: any, b: any) => a.beneficiary_account_name.localeCompare(b.beneficiary_account_name, 'en', { numeric: true }),
        ellipsis: true,
        render: (value: string) => (
          <Tooltip title={value}>
            <span style={{ fontSize: '12px' }}>
              {value !== '' ? value : '-'}
            </span>
          </Tooltip>
        )
      },
      {
        title: 'Bank',
        dataIndex: 'beneficiary_bank_name',
        key: 'beneficiary_bank_name',
        width: 120,
        responsive: ['sm', 'md', 'lg', 'xl'],
        sorter: (a: any, b: any) => a.beneficiary_bank_name.localeCompare(b.beneficiary_bank_name, 'en', { numeric: true }),
        ellipsis: true,
        render: (value: string) => (
          <Tag color="blue" style={{ margin: 0, fontSize: '12px' }}>
            {value !== '' ? value : 'N/A'}
          </Tag>
        )
      },
      {
        title: 'Amount (RWF)',
        dataIndex: 'amount',
        key: 'amount',
        width: 100,
        responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        sorter: (a: any, b: any) => parseFloat(a.amount) - parseFloat(b.amount),
        ellipsis: true,
        render: (value: string) => (
          <span style={{ fontWeight: 'bold', color: '#1890ff', fontSize: '12px' }}>
            {value !== '' ? parseFloat(value).toLocaleString() : '0'}
          </span>
        )
      },
      {
        title: 'Reason',
        dataIndex: 'reason',
        key: 'reason',
        width: 150,
        responsive: ['md', 'lg', 'xl'],
        ellipsis: true,
        render: (value: string) => (
          <Tooltip title={value}>
            <span style={{ fontSize: '12px' }}>
              {value !== '' ? value : '-'}
            </span>
          </Tooltip>
        )
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        render: (value: string, record: JSPADataType) => {
          const statusConfig = {
            COMPLETED: { color: 'success', icon: <CheckCircleOutlined />, label: 'Completed Refund' },
            APPROVED: { color: 'success', icon: <CheckCircleOutlined />, label: 'Completed Refund' },
            PENDING: { color: 'warning', icon: <ClockCircleOutlined />, label: 'Pending Refund' },
            INITIATED: { color: 'processing', icon: <SyncOutlined />, label: 'Initiated Refund' },
            'RECEIVED REQUEST': { color: 'processing', icon: <InfoCircleOutlined />, label: 'Received Request' },
            REJECTED: { color: 'error', icon: <CloseCircleOutlined />, label: 'Rejected' },
            'Downloaded to be Refunded': { color: 'success', icon: <CheckCircleOutlined />, label: 'Completed Refund' }
          }

          const config = statusConfig[record?.status as keyof typeof statusConfig] ?? {
            color: 'default',
            icon: null,
            label: record?.status
          }

          return (
            <Tag color={config.color} icon={config.icon} style={{ fontSize: '12px' }}>
              {config.label}
            </Tag>
          )
        },
        sorter: (a: any, b: any) => a.status.localeCompare(b.status, 'en', { numeric: true }),
        ellipsis: true,
        filters: uniqueStatuses.map(status => ({ text: status, value: status })),
        onFilter: (value: any, record: JSPADataType) => record.status === value
      },
      {
        title: 'Created Date',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 100,
        responsive: ['sm', 'md', 'lg', 'xl'],
        sorter: (a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        ellipsis: true,
        render: (value: string): string => value !== '' ? new Date(value).toLocaleDateString() : '-'
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 60,
        fixed: 'right',
        responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        render: (_, record: JSPADataType) => (
          <Space size="small">
            <Tooltip title="View Details">
              <Button
                type="text"
                icon={<EyeOutlined />}
                size="small"
                onClick={() => {
                  // Add view details functionality
                  // eslint-disable-next-line no-console
                  console.log('View details for:', record.transaction_reference)
                }}
              />
            </Tooltip>
          </Space>
        )
      }
    ]

    // Combine all columns
    return [...baseColumns, ...conditionalColumns, ...commonColumns]
  }

  // Generate dynamic columns based on current data
  const columnsJSPA = generateDynamicColumns(jspaData)

  const columnsBidsSecurity: ColumnsType<DataType> = [
    {
      title: 'No.',
      dataIndex: 'index',
      key: 'index',
      width: 50,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      sorter: (a, b) => a.index - b.index,
      ellipsis: false
    },
    {
      title: 'Bidder Name',
      dataIndex: 'full_name',
      key: 'full_name',
      width: 150,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      render: (_text: any, record: any) => {
        const fullName = record?.full_name
        return (
          <Tooltip title={fullName}>
            <span style={{ fontSize: '12px' }}>
              {fullName != null ? fullName : 'No Name Provided'}
            </span>
          </Tooltip>
        )
      },
      sorter: (a: any, b: any) => {
        const nameA = a?.full_name ?? ''
        const nameB = b?.full_name ?? ''
        return nameA.localeCompare(nameB)
      }
    },
    {
      title: 'Phone No',
      dataIndex: 'phone_number',
      key: 'phone_number',
      width: 120,
      responsive: ['sm', 'md', 'lg', 'xl'],
      render: (_text: any, record: any) => (
            <span style={{ fontSize: '12px' }}>{record?.phone_number}</span>
      ),
      ellipsis: true
    },
    {
      title: 'Bidder National ID',
      dataIndex: 'nid',
      key: 'nid',
      width: 120,
      responsive: ['md', 'lg', 'xl'],
      render: (_text: any, record: any) => (
        <span style={{ fontSize: '12px' }}>{record?.nid}</span>
      ),
      ellipsis: false
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      render: (_text: any, record: any) => {
        const formattedAmount = new Intl.NumberFormat().format(record?.amount)
        return (
          <span style={{ fontSize: '12px' }}>
            {formattedAmount}
          </span>
        )
      },
      sorter: (a: any, b: any) => parseFloat(a?.amount) - parseFloat(b?.amount),
      ellipsis: true
    },
    {
      title: 'Payment Date',
      dataIndex: 'payment_date_time',
      key: 'payment_date_time',
      width: 120,
      responsive: ['sm', 'md', 'lg', 'xl'],
      render: (_text: any, record: any) => (
        <span style={{ fontSize: '12px' }}>{record?.payment_date_time}</span>
      ),
      sorter: (a: any, b: any) => a?.payment_date_time.localeCompare(b?.payment_date_time),
      ellipsis: true
    }
  ]

  const columns: ColumnsType<DataType> = [
    {
      title: 'No.',
      dataIndex: 'index',
      key: 'index',
      width: 50,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      sorter: (a, b) => a.index - b.index,
      ellipsis: false
    },
    {
      title: 'Transaction Reference',
      dataIndex: 'transaction_reference',
      key: 'transaction_reference',
      width: 100,
      responsive: ['sm', 'md', 'lg', 'xl'],
      sorter: (a: any, b: any) => a?.transaction_reference.localeCompare(b?.transaction_reference),
      ellipsis: true,
      render: (value: string) => (
        <Tooltip title={value}>
          <span style={{ fontSize: '12px' }}>{value}</span>
        </Tooltip>
      )
    },
    {
      title: 'Beneficiary Account Name',
      dataIndex: 'beneficiary_account_name',
      key: 'beneficiary_account_name',
      width: 150,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      sorter: (a: any, b: any) => a?.beneficiary_account_name.localeCompare(b?.beneficiary_account_name),
      ellipsis: true,
      render: (value: string) => (
        <Tooltip title={value}>
          <span style={{ fontSize: '12px' }}>{value}</span>
        </Tooltip>
      )
    },
    {
      title: 'Beneficiary Bank Name',
      dataIndex: 'beneficiary_bank_name',
      key: 'beneficiary_bank_name',
      width: 140,
      responsive: ['sm', 'md', 'lg', 'xl'],
      sorter: (a: any, b: any) => a?.beneficiary_bank_name.localeCompare(b?.beneficiary_bank_name),
      ellipsis: true,
      render: (value: string) => (
        <Tag color="blue" style={{ fontSize: '12px' }}>
          {value}
        </Tag>
      )
    },
    {
      title: 'Beneficiary Account Number',
      dataIndex: 'beneficiary_account_number',
      key: 'beneficiary_account_number',
      width: 120,
      responsive: ['md', 'lg', 'xl'],
      sorter: (a: any, b: any) => a?.beneficiary_account_number.localeCompare(b?.beneficiary_account_number),
      ellipsis: true,
      render: (value: string) => (
        <span style={{ fontSize: '12px' }}>{value}</span>
      )
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      render: (_text: any, record: any) => {
        const amount = parseInt(record?.amount, 10)
        return (
          <span style={{ fontSize: '12px' }}>
            {amount.toLocaleString()}
          </span>
        )
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      render: (_text: any, record: any) => (
        <span style={{ fontSize: '12px' }}>{record?.status}</span>
      ),
      sorter: (a: any, b: any) => a.status.localeCompare(b.status),
      ellipsis: true
    }
  ]

  // Generate comprehensive report columns
  const generateComprehensiveColumns = (): ColumnsType<ComprehensiveReportDataType> => [
    {
      title: 'No.',
      dataIndex: 'index',
      key: 'index',
      width: 60,
      sorter: (a, b) => a.index - b.index,
      ellipsis: true,
      fixed: 'left'
    },
    {
      title: 'Type',
      dataIndex: 'table_source',
      key: 'table_source',
      width: 120,
      render: (value: string) => (
        <Badge
          status={value === 'refund_request' ? 'processing' : value === 'bailiff_refund_request' ? 'success' : 'default'}
          text={value.replace('_', ' ').toUpperCase()}
        />
      ),
      filters: uniqueTableSources.map(source => ({ text: source.replace('_', ' ').toUpperCase(), value: source })),
      onFilter: (value: any, record: ComprehensiveReportDataType) => record.table_source === value
    },
    {
      title: 'Transaction Ref',
      dataIndex: 'transaction_reference',
      key: 'transaction_reference',
      width: 120,
      ellipsis: true,
      render: (value: string) => value !== '' ? value : '-'
    },
    {
      title: 'Name',
      dataIndex: 'bidder_full_name',
      key: 'bidder_full_name',
      width: 150,
      ellipsis: true,
      render: (value: string, record) => value !== '' ? value : (record.full_name !== '' ? record.full_name : '-')
    },
    {
      title: 'Account',
      dataIndex: 'beneficiary_account_name',
      key: 'beneficiary_account_name',
      width: 140,
      ellipsis: true,
      render: (value: string) => value !== '' ? value : '-'
    },
    {
      title: 'Bank',
      dataIndex: 'beneficiary_bank_name',
      key: 'beneficiary_bank_name',
      width: 120,
      ellipsis: true,
      render: (value: string) => value !== '' ? <Tag color="blue">{value}</Tag> : '-',
      filters: uniqueBanks.map(bank => ({ text: bank, value: bank })),
      onFilter: (value: any, record: ComprehensiveReportDataType) => record.beneficiary_bank_name === value
    },
    {
      title: 'Amount (RWF)',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      sorter: (a, b) => parseFloat(String(a.amount)) - parseFloat(String(b.amount)),
      render: (value: string | number) => (
        <span>
          {typeof value === 'string' ? parseFloat(value).toLocaleString() : value.toLocaleString()}
        </span>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (value: string) => {
        const statusConfig = {
          COMPLETED: { color: 'success', icon: <CheckCircleOutlined />, label: 'Completed Refund' },
          APPROVED: { color: 'success', icon: <CheckCircleOutlined />, label: 'Completed Refund' },
          PENDING: { color: 'warning', icon: <ClockCircleOutlined />, label: 'Pending Refund' },
          INITIATED: { color: 'processing', icon: <SyncOutlined />, label: 'Initiated Refund' },
          'RECEIVED REQUEST': { color: 'processing', icon: <InfoCircleOutlined />, label: 'Received Request' },
          REJECTED: { color: 'error', icon: <CloseCircleOutlined />, label: 'Rejected' },
          'Downloaded to be Refunded': { color: 'success', icon: <CheckCircleOutlined />, label: 'Completed Refund' }
        }
        const config = statusConfig[value as keyof typeof statusConfig] ?? {
          color: 'default',
          icon: null,
          label: value
        }
        return <Tag color={config.color} icon={config.icon}>{config.label}</Tag>
      },
      filters: uniqueStatuses.map(status => ({ text: status, value: status })),
      onFilter: (value: any, record: ComprehensiveReportDataType) => record.status === value
    },
    {
      title: 'Created Date',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 100,
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (value: string) => value !== '' ? new Date(value).toLocaleDateString() : '-'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record: ComprehensiveReportDataType) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => {
                // eslint-disable-next-line no-console
                console.log('View details for:', record.transaction_reference)
              }}
            />
          </Tooltip>
        </Space>
      )
    }
  ]

  // Generate dynamic columns for each tab based on data structure
  const generateDynamicTabColumns = (data: any[], tabType: string): ColumnsType<any> => {
    const dynamicColumns: ColumnsType<any> = []

    // Always add index column first
    dynamicColumns.push({
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 40,
      fixed: 'left'
    })

    // If no data, still return columns with index so table structure is visible
    if (data == null || data.length === 0) {
      // eslint-disable-next-line no-console
      console.warn(`⚠️ No data available for tab ${tabType}, generating minimal columns`)
      return dynamicColumns
    }

    const sampleItem = data[0]

    // Note: Status and Amount columns are now included in predetermined column lists below
    // They will be added in their predetermined positions to maintain consistent ordering

    // Define specific columns for different tab types with predetermined order
    const getTabSpecificColumns = (tabTypeParam: string): string[] => {
      switch (tabTypeParam) {
        case 'bidders':
          // Predetermined columns for Bidder Requests tab
          return [
            'created_at',
            'transaction_reference',
            'beneficiary_account_name',
            'beneficiary_bank_name',
            'beneficiary_account_number',
            'amount',
            'status',
            'phone_number',
            'email',
            'updated_at'
          ]
        case 'bailiffs':
          // Predetermined columns for Bailiff Requests tab
          return [
            'created_at',
            'transaction_reference',
            'beneficiary_account_name',
            'beneficiary_bank_name',
            'beneficiary_account_number',
            'amount',
            'status',
            'bailiff_assignee',
            'bailiff_national_id',
            'bailiff_phone_number',
            'bailiff_email',
            'updated_at'
          ]
        case 'bidSecurityPayments':
          return [
            'full_name',
            'payment_date_time',
            'ref_number',
            'name',
            'security_amount',
            'execution_case_no',
            'property_id'
          ]
        case 'comprehensive':
          // Predetermined columns for All Refund Request tab - same as Bidder Requests tab
          return [
            'created_at',
            'transaction_reference',
            'beneficiary_account_name',
            'beneficiary_bank_name',
            'beneficiary_account_number',
            'amount',
            'status',
            'phone_number',
            'email',
            'updated_at'
          ]
        default:
          return []
      }
    }

    // Get specific columns for this tab type
    const specificColumns = getTabSpecificColumns(tabType)

    // Track which columns we've already added to avoid duplicates
    const addedColumns = new Set<string>(['index'])

    // Add predetermined columns in specified order
    if (specificColumns.length > 0) {
      specificColumns.forEach(key => {
        // Skip if already added (shouldn't happen with predetermined lists, but safety check)
        if (addedColumns.has(key)) {
          return
        }
        addedColumns.add(key)
        // Always add the column even if sampleItem doesn't have it (data might have it)
        const columnConfig: any = {
          title: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          dataIndex: key,
          key: key,
          width: 120,
          ellipsis: true,
          render: (text: any, record: any) => {
            // Check record first, then fall back to text
            const value = record[key] ?? text
            return (
              <div style={{
                wordWrap: 'break-word',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                maxWidth: '120px',
                fontSize: '13.5px',
                fontFamily: 'inherit',
                lineHeight: '1.3',
                padding: '4px 0'
              }}>
                {value !== null && value !== undefined && value !== '' ? String(value) : '-'}
              </div>
            )
          }
        }

        // Special handling for specific fields with predetermined formatting
        if (key.includes('date') || key.includes('created_at') || key.includes('updated_at') || key.includes('payment_date_time')) {
          columnConfig.width = 140
          columnConfig.render = (date: string, record: any) => {
            const dateValue = record[key] ?? date
            return (
              <span style={{ fontSize: '12px', fontFamily: 'inherit' }}>
                {dateValue !== '' && dateValue != null ? dayjs(dateValue).format('YYYY-MM-DD HH:mm') : '-'}
              </span>
            )
          }
          columnConfig.sorter = (a: any, b: any) => {
            const dateA = a[key] != null && a[key] !== '' ? dayjs(a[key]).unix() : 0
            const dateB = b[key] != null && b[key] !== '' ? dayjs(b[key]).unix() : 0
            return dateA - dateB
          }
        } else if (key.includes('name') || key.includes('full_name') || key.includes('assignee') || key === 'beneficiary_account_name') {
          columnConfig.width = 150
          columnConfig.render = (text: string, record: any) => {
            const value = record[key] ?? text
            return (
              <div style={{
                wordWrap: 'break-word',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                maxWidth: '150px',
                fontSize: '13.5px',
                fontFamily: 'inherit',
                lineHeight: '1.3',
                padding: '4px 0'
              }}>
                {value !== '' && value != null ? String(value) : '-'}
              </div>
            )
          }
        } else if (key.includes('email') || key === 'bailiff_email') {
          columnConfig.width = 150
          columnConfig.render = (email: string, record: any) => {
            const emailValue = record[key] ?? email
            const emailStr = typeof emailValue === 'string' ? emailValue : String(emailValue ?? '')
            return emailStr !== '' && emailValue != null
              ? <a href={`mailto:${emailStr}`} style={{ fontSize: '12px', fontFamily: 'inherit' }}>{emailStr}</a>
              : '-'
          }
        } else if (key.includes('phone') || key === 'bailiff_phone_number') {
          columnConfig.width = 110
          columnConfig.render = (phone: string, record: any) => {
            const phoneValue = record[key] ?? phone
            const phoneStr = typeof phoneValue === 'string' ? phoneValue : String(phoneValue ?? '')
            return phoneStr !== '' && phoneValue != null
              ? <a href={`tel:${phoneStr}`} style={{ fontSize: '12px', fontFamily: 'inherit' }}>{phoneStr}</a>
              : '-'
          }
        } else if (key.includes('reference') || key.includes('id') || key === 'transaction_reference' || key === 'ref_number') {
          columnConfig.width = 130
          columnConfig.render = (value: string, record: any) => {
            const refValue = record[key] ?? value
            return (
              <span style={{ fontFamily: 'inherit' }}>
                {refValue !== '' && refValue != null ? String(refValue) : '-'}
              </span>
            )
          }
        } else if (key === 'amount' || key === 'security_amount') {
          columnConfig.width = 110
          columnConfig.render = (amount: string | number, record: any) => {
            const amountValue = record[key] ?? amount
            const numAmount = typeof amountValue === 'string' ? parseFloat(amountValue) : amountValue
            return (
              <span style={{ fontSize: '12px', fontFamily: 'inherit', fontWeight: 500 }}>
                {!isNaN(numAmount) && numAmount != null ? numAmount.toLocaleString() : '-'}
              </span>
            )
          }
          columnConfig.sorter = (a: any, b: any) => {
            const amountA = typeof a[key] === 'string' ? parseFloat(a[key]) : a[key]
            const amountB = typeof b[key] === 'string' ? parseFloat(b[key]) : b[key]
            return (amountA ?? 0) - (amountB ?? 0)
          }
        } else if (key === 'status') {
          // Status is already added above, but ensure it's in the right position
          columnConfig.width = 130
          columnConfig.filters = [
            { text: 'Completed', value: 'COMPLETED' },
            { text: 'Pending', value: 'PENDING' },
            { text: 'Received Request', value: 'RECEIVED REQUEST' },
            { text: 'Rejected', value: 'REJECTED' },
            { text: 'Bank Transfer Failed', value: 'Bank Transfer Failed' },
            { text: 'Downloaded to be Refunded', value: 'Downloaded to be Refunded' },
            { text: 'Refunded', value: 'REFUNDED' }
          ]
          columnConfig.onFilter = (value: any, record: any) => {
            if (value === 'COMPLETED') {
              return record.status === 'COMPLETED' || record.status === 'APPROVED'
            }
            return record.status === value
          }
          columnConfig.render = (status: string, record: any) => {
            const statusValue = record.status ?? status
            return <span style={{ fontSize: '12px' }}>{statusValue ?? '-'}</span>
          }
        } else if (key === 'table_source') {
          columnConfig.width = 120
          columnConfig.render = (source: string, record: any) => {
            const sourceValue = record[key] ?? source
            return (
              <Tag color={sourceValue === 'refund_request' ? 'blue' : sourceValue === 'bailiff_refund_request' ? 'green' : 'default'} style={{ fontSize: '12px' }}>
                {sourceValue !== '' && sourceValue != null ? String(sourceValue).replace(/_/g, ' ') : '-'}
              </Tag>
            )
          }
        } else if (key === 'beneficiary_account_number' || key === 'beneficiary_bank_name') {
          columnConfig.width = 140
          columnConfig.render = (text: string, record: any) => {
            const value = record[key] ?? text
            return (
              <span style={{ fontSize: '12px', fontFamily: 'inherit' }}>
                {value !== '' && value != null ? String(value) : '-'}
              </span>
            )
          }
        }

        dynamicColumns.push(columnConfig)
      })
    } else {
      // Fallback: add all available columns for other tab types
      Object.keys(sampleItem).forEach(key => {
        if (key === 'index' || key === 'status' || key === 'amount' || key === 'key') {
          return // Skip already handled columns
        }

        const columnConfig: any = {
          title: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          dataIndex: key,
          key: key,
          width: 120,
          ellipsis: true,
          render: (text: any) => (
            <div style={{
              wordWrap: 'break-word',
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              maxWidth: '120px',
              fontSize: '13.5px',
              fontFamily: 'inherit'
            }}>
              {text !== null && text !== undefined ? text : '-'}
            </div>
          )
        }

        // Special handling for specific fields
        if (key.includes('date') || key.includes('created_at') || key.includes('updated_at')) {
          columnConfig.render = (date: string) => (
            <span style={{ fontSize: '12px', fontFamily: 'inherit' }}>
              {date !== '' ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-'}
            </span>
          )
          columnConfig.sorter = (a: any, b: any) => dayjs(a[key]).unix() - dayjs(b[key]).unix()
        } else if (key.includes('name') || key.includes('full_name')) {
          columnConfig.width = 150
          columnConfig.render = (text: string) => (
            <div style={{
              wordWrap: 'break-word',
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              maxWidth: '150px',
              fontSize: '13.5px',
              fontFamily: 'inherit'
            }}>
              {text !== '' ? text : '-'}
            </div>
          )
        } else if (key.includes('email')) {
          columnConfig.width = 140
          columnConfig.render = (email: string) => email !== '' ? <a href={`mailto:${email}`} style={{ fontSize: '12px', fontFamily: 'inherit' }}>{email}</a> : '-'
        } else if (key.includes('phone')) {
          columnConfig.width = 100
          columnConfig.render = (phone: string) => phone !== '' ? <a href={`tel:${phone}`} style={{ fontSize: '12px', fontFamily: 'inherit' }}>{phone}</a> : '-'
        } else if (key.includes('reference') || key.includes('id')) {
          columnConfig.width = 120
          columnConfig.render = (value: string) => (
            <span style={{ fontFamily: 'inherit' }}>
              {value !== '' ? value : '-'}
            </span>
          )
        }

        dynamicColumns.push(columnConfig)
      })
    }

    // Actions column removed for cleaner, more compact display

    return dynamicColumns
  }

  const notRequestedBidSecurityColumns = useMemo((): ColumnsType<NotRequestedBidSecurityRow> => [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 44,
      fixed: 'left'
    },
    {
      title: 'Reference number',
      dataIndex: 'reference_number',
      key: 'reference_number',
      width: 170,
      ellipsis: true,
      render: (_v: string, row: NotRequestedBidSecurityRow) => {
        const ref = row.reference_number ?? row.transaction_reference ?? row.bid_security_ref_number
        return (
          <span style={{ fontFamily: 'inherit' }}>{ref !== '' && ref != null ? String(ref) : '-'}</span>
        )
      }
    },
    {
      title: 'Bidder',
      dataIndex: 'bidder_full_name',
      key: 'bidder_full_name',
      width: 160,
      ellipsis: true
    },
    {
      title: 'Phone',
      dataIndex: 'bidder_phone_number',
      key: 'bidder_phone_number',
      width: 120,
      render: (v: string) => (v !== '' && v != null ? <a href={`tel:${v}`}>{v}</a> : '-')
    },
    {
      title: 'Email',
      dataIndex: 'bidder_email',
      key: 'bidder_email',
      width: 160,
      ellipsis: true,
      render: (v: string) => (v !== '' && v != null ? <a href={`mailto:${v}`}>{v}</a> : '-')
    },
    {
      title: 'NID',
      dataIndex: 'bidder_nid',
      key: 'bidder_nid',
      width: 120,
      ellipsis: true
    },
    {
      title: 'Property ID',
      dataIndex: 'property_id',
      key: 'property_id',
      width: 120,
      ellipsis: true
    },
    {
      title: 'Execution case',
      dataIndex: 'execution_case_no',
      key: 'execution_case_no',
      width: 120,
      ellipsis: true
    },
    {
      title: 'Payer code',
      dataIndex: 'payer_code',
      key: 'payer_code',
      width: 100,
      ellipsis: true
    },
    {
      title: 'Payment amount',
      dataIndex: 'payment_amount',
      key: 'payment_amount',
      width: 120,
      render: (v: string | number) => {
        const n = typeof v === 'string' ? parseFloat(v) : v
        return !isNaN(Number(n)) && n != null ? Number(n).toLocaleString() : '-'
      }
    },
    {
      title: 'Currency',
      dataIndex: 'currency',
      key: 'currency',
      width: 80
    },
    {
      title: 'Payment date',
      dataIndex: 'payment_date_time',
      key: 'payment_date_time',
      width: 150,
      render: (v: string) => (v !== '' && v != null ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-')
    },
    {
      title: 'Auction status',
      dataIndex: 'auction_status',
      key: 'auction_status',
      width: 120,
      render: (v: string) => (v !== '' && v != null ? <Tag>{String(v)}</Tag> : '-')
    }
  ], [])

  // Generate tab configurations with dynamic columns
  const tabConfigurations = useMemo(() => {
    // eslint-disable-next-line no-console
    console.log('🔄 Generating tab configurations - comprehensiveData length:', comprehensiveData.length)
    // Enhanced debugging for comprehensive data
    // eslint-disable-next-line no-console
    console.log('🔍 Comprehensive Tab Debug:')
    // eslint-disable-next-line no-console
    console.log('📊 Total comprehensiveData length:', comprehensiveData.length)
    // eslint-disable-next-line no-console
    console.log('📊 ComprehensiveData sample:', comprehensiveData.slice(0, 2))

    // Filter comprehensive data based on table source
    // All Refund Request tab: show only data from 'refund_request' and 'bailiff_refund_request' table sources
    const allRefundData = comprehensiveData.filter(item => {
      const tableSource = item.table_source
      const hasRefundId = item.refund_request_id != null && String(item.refund_request_id).trim() !== '' && String(item.refund_request_id) !== 'null'
      const hasBailiffId = item.bailiff_refund_request_id != null && String(item.bailiff_refund_request_id).trim() !== '' && String(item.bailiff_refund_request_id) !== 'null'

      // Accept both explicit table source and reliable id-based inference.
      return tableSource === 'refund_request' || tableSource === 'bailiff_refund_request' || hasRefundId || hasBailiffId
    })

    // Bidder Requests tab: filter data by table_source OR refund_request_id field
    const biddersFilteredData = comprehensiveData.filter(item => {
      const tableSource = item.table_source
      const refundId = item.refund_request_id

      // Primary filter: table_source
      if (tableSource === 'refund_request') {
        return true
      }

      // Fallback: check refund_request_id field
      const hasRefundId = refundId != null && String(refundId).trim() !== '' && String(refundId) !== 'null'
      return hasRefundId
    })

    // Bailiff Requests tab: filter data by table_source OR bailiff_refund_request_id field
    const bailiffsFilteredData = comprehensiveData.filter(item => {
      const tableSource = item.table_source
      const bailiffId = item.bailiff_refund_request_id

      // Primary filter: table_source
      if (tableSource === 'bailiff_refund_request') {
        return true
      }

      // Fallback: check bailiff_refund_request_id field
      const hasBailiffId = bailiffId != null && String(bailiffId).trim() !== '' && String(bailiffId) !== 'null'
      return hasBailiffId
    })

    // eslint-disable-next-line no-console
    console.log('📊 All Refund Data length:', allRefundData.length)
    // eslint-disable-next-line no-console
    console.log('📊 Bidders Data length (with refund_request_id):', biddersFilteredData.length)
    // eslint-disable-next-line no-console
    console.log('📊 Bailiffs Data length (with bailiff_refund_request_id):', bailiffsFilteredData.length)

    const baseTabs = [
      {
        key: 'comprehensive',
        title: 'All Refund Request',
        columns: generateDynamicTabColumns(allRefundData, 'comprehensive') as ColumnsType<TableDataType>,
        data: allRefundData as TableDataType[]
      },
      {
        key: 'bidders',
        title: 'Bidder Requests',
        columns: generateDynamicTabColumns(biddersFilteredData, 'bidders') as ColumnsType<TableDataType>,
        data: biddersFilteredData as TableDataType[]
      },
      {
        key: 'bailiffs',
        title: 'Bailiff Requests',
        columns: generateDynamicTabColumns(bailiffsFilteredData, 'bailiffs') as ColumnsType<TableDataType>,
        data: bailiffsFilteredData as TableDataType[]
      },
      {
        key: 'not_requested_bid_security',
        title: 'No bid security refund request',
        columns: notRequestedBidSecurityColumns as ColumnsType<TableDataType>,
        data: notRequestedBidSecurityData as TableDataType[]
      }
    ]

    // Add data source specific tabs from comprehensive data (excluding bailiff_refund_request, bid_security_payment, refund_request)
    if (Object.keys(tableSourceBreakdown).length > 0) {
      const dataSourceTabs = Object.keys(tableSourceBreakdown)
        .filter(source =>
          source !== 'bailiff_refund_request' &&
          source !== 'bid_security_payment' &&
          source !== 'refund_request'
        )
        .map(source => {
          const filteredData = comprehensiveData.filter(item => item.table_source === source)
          return {
            key: `source_${source}`,
            title: `${source.replace('_', ' ').toUpperCase()} (${tableSourceBreakdown[source]})`,
            columns: generateDynamicTabColumns(filteredData, `source_${source}`) as ColumnsType<TableDataType>,
            data: filteredData as TableDataType[]
          }
        })

      return [...baseTabs, ...dataSourceTabs]
    }

    return baseTabs
  }, [comprehensiveData, tableSourceBreakdown, notRequestedBidSecurityData, notRequestedBidSecurityColumns])

  return (
    <div
      className="reporting-process-container"
      style={{
        width: '100%',
        maxWidth: '100%',
        margin: 0,
        padding: 0,
        boxSizing: 'border-box',
        minHeight: '100vh',
        overflowX: 'clip'
      }}
    >
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .reporting-process-container {
            --rp-primary: var(--refund-theme-primary, #37762f);
            --rp-primary-mid: #4a7c59;
            --rp-soft: var(--refund-theme-primary-soft, #dff0de);
            --rp-border: var(--refund-theme-border, #e5e7eb);
            background:
              radial-gradient(ellipse 100% 70% at 100% -15%, rgba(55, 118, 47, 0.1), transparent 52%),
              radial-gradient(ellipse 80% 50% at 0% 100%, rgba(55, 118, 47, 0.06), transparent 48%),
              linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
          }
          .reporting-process-inner {
            width: 100%;
            max-width: 100%;
            margin-left: 0;
            margin-right: 0;
            padding-left: clamp(8px, 2vw, 20px);
            padding-right: clamp(20px, 3vw, 40px);
            padding-bottom: 28px;
            box-sizing: border-box;
          }
          @media (max-width: 576px) {
            .reporting-process-inner {
              padding-left: 8px;
              padding-right: 16px;
            }
          }
          .reporting-process-container .rp-hero {
            position: relative;
            margin-bottom: clamp(16px, 2.5vw, 24px);
            border-radius: 0 0 16px 16px;
            border: 1px solid var(--rp-border);
            border-top: none;
            background: linear-gradient(135deg, #ffffff 0%, var(--rp-soft) 50%, #ecfdf5 100%);
            box-shadow: 0 4px 20px rgba(55, 118, 47, 0.08);
            overflow: hidden;
          }
          .reporting-process-container .rp-hero::before {
            content: '';
            position: absolute;
            left: 0;
            right: 0;
            top: 0;
            height: 4px;
            background: linear-gradient(90deg, var(--rp-primary), var(--rp-primary-mid), var(--rp-primary));
          }
          .reporting-process-container .rp-hero-inner {
            position: relative;
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
            padding: clamp(14px, 2vw, 22px) clamp(14px, 2vw, 20px);
          }
          .reporting-process-container .rp-hero-title-wrap {
            display: flex;
            align-items: center;
            gap: 14px;
            min-width: 0;
          }
          .reporting-process-container .rp-hero-icon {
            width: 48px;
            height: 48px;
            border-radius: 14px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(145deg, var(--rp-primary) 0%, var(--rp-primary-mid) 100%);
            color: #fff;
            font-size: 24px;
            flex-shrink: 0;
            box-shadow: 0 8px 22px rgba(55, 118, 47, 0.32);
          }
          .reporting-process-container .rp-hero-title {
            margin: 0;
            font-size: clamp(1.05rem, 2.2vw, 1.35rem);
            font-weight: 700;
            color: #1f2937;
            letter-spacing: -0.02em;
          }
          .reporting-process-container .rp-hero-sub {
            margin: 4px 0 0;
            font-size: clamp(0.8rem, 1.5vw, 0.9rem);
            color: #64748b;
            line-height: 1.45;
            max-width: 42ch;
          }
          .reporting-process-container .rp-section-title {
            margin: clamp(12px, 2vw, 20px) 0 clamp(10px, 1.5vw, 14px);
            font-weight: 700;
            font-size: clamp(0.95rem, 1.8vw, 1.1rem);
            color: var(--rp-primary);
            display: flex;
            align-items: center;
            gap: 10px;
          }
          .reporting-process-container .rp-stat-card.ant-card {
            width: 100%;
            height: 100%;
            border-radius: 14px;
            border: 1px solid var(--rp-border);
            box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
            transition: box-shadow 0.2s ease, border-color 0.2s ease;
          }
          .reporting-process-container .rp-stat-card.ant-card:hover {
            border-color: rgba(55, 118, 47, 0.35);
            box-shadow: 0 8px 24px rgba(55, 118, 47, 0.12);
          }
          .reporting-process-container .rp-stat-card .ant-card-body {
            padding: clamp(12px, 2vw, 16px);
          }
          .reporting-process-container .rp-filters {
            width: 100%;
            border-radius: 14px;
            border: 1px solid var(--rp-border);
            background: #fff;
            box-shadow: 0 2px 12px rgba(15, 23, 42, 0.05);
            padding: clamp(12px, 2vw, 18px);
            box-sizing: border-box;
          }
          .reporting-process-container .rp-filters legend {
            padding: 0 8px;
            color: var(--rp-primary);
            font-weight: 700;
            font-size: clamp(0.8rem, 1.4vw, 0.9rem);
          }
          .reporting-process-container .rp-main-card.ant-card {
            width: 100%;
            margin-left: auto;
            margin-right: auto;
            border-radius: 14px;
            border: 1px solid var(--rp-border);
            box-shadow: 0 6px 24px rgba(15, 23, 42, 0.07);
            overflow: hidden;
          }
          .reporting-process-container .rp-main-card .ant-card-head {
            border-bottom: 1px solid rgba(55, 118, 47, 0.12);
          }
          .reporting-process-container .rp-main-card .ant-card-body {
            padding: clamp(10px, 1.5vw, 16px) clamp(10px, 1.5vw, 16px) 16px clamp(4px, 0.8vw, 10px);
          }
          .reporting-process-container .rp-data-table {
            width: 100%;
            max-width: 100%;
            margin-left: 0;
          }
          .reporting-process-container .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab {
            transition: background 0.2s ease, color 0.2s ease;
            border-radius: 8px 8px 0 0 !important;
          }
          .reporting-process-container .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab:hover {
            background: rgba(55, 118, 47, 0.08);
            color: var(--rp-primary);
          }
          .reporting-process-container .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab-active {
            background: #fff !important;
            border-bottom-color: #fff !important;
            color: var(--rp-primary) !important;
            font-weight: 600;
            box-shadow: 0 -2px 12px rgba(55, 118, 47, 0.1);
          }
          .reporting-process-container .ant-tabs-nav {
            margin-bottom: 12px !important;
          }
          .ant-tabs-tab {
            transition: all 0.3s ease;
          }
          .reporting-process-container .ant-statistic-title {
            white-space: normal !important;
            word-break: break-word !important;
            line-height: 1.3 !important;
            min-height: 34px;
            font-size: clamp(11px, 1.2vw, 13px) !important;
          }
          .reporting-process-container .ant-statistic-content-value {
            font-size: clamp(16px, 2.2vw, 24px) !important;
          }
          /* Mobile: tabs scroll horizontally */
          .reporting-process-container .ant-tabs-nav-wrap {
            overflow-x: auto;
          }
          @media (max-width: 768px) {
            .reporting-process-container .rp-hero-inner {
              flex-direction: column;
              align-items: flex-start;
              gap: 12px;
            }
            .reporting-process-container .rp-hero-icon {
              width: 40px;
              height: 40px;
              font-size: 20px;
            }
            .reporting-process-container .rp-stat-card .ant-card-body {
              padding: 12px;
            }
            .reporting-process-container .ant-tabs-tab {
              padding: 6px 10px !important;
              font-size: 12px;
            }
            .reporting-process-container .rp-filters {
              padding: 10px;
            }
          }
          @media (max-width: 480px) {
            .reporting-process-container .rp-hero-title {
              font-size: 1rem;
            }
            .reporting-process-container .rp-hero-sub {
              font-size: 0.75rem;
            }
            .reporting-process-container .ant-statistic-content-value {
              font-size: 15px !important;
            }
            .reporting-process-container .ant-tabs > .ant-tabs-nav .ant-tabs-nav-list {
              flex-wrap: nowrap;
              overflow-x: auto;
            }
            .reporting-process-container .ant-card-head-title {
              padding: 8px 0 !important;
            }
            .reporting-process-container .rp-main-card .ant-card-body {
              padding: 8px 6px !important;
            }
          }
          /* Ensure tables always have horizontal scroll on small screens */
          .reporting-process-container .ant-table-wrapper {
            overflow-x: auto;
          }
          /* Make tab labels wrap text on very small screens */
          @media (max-width: 360px) {
            .reporting-process-container .ant-tabs-tab {
              padding: 4px 6px !important;
              font-size: 11px;
            }
          }
          /* Responsive download buttons section */
          @media (max-width: 576px) {
            .reporting-process-container .actions .ant-space-item {
              width: 100%;
            }
            .reporting-process-container .actions .ant-space-item .ant-btn {
              width: 100%;
              justify-content: center;
            }
          }
        `}
      </style>
      <div className="reporting-process-inner">
      <div className="rp-hero">
        <div className="rp-hero-inner">
          <div className="rp-hero-title-wrap">
            <div className="rp-hero-icon" aria-hidden>
              <BarChartOutlined />
            </div>
            <div style={{ minWidth: 0 }}>
              <h1 className="rp-hero-title">Reporting &amp; analytics</h1>
              <p className="rp-hero-sub">
                Refund request insights, exports, and tabular data. Use filters to narrow results; tables scroll horizontally on smaller screens.
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() => { void refreshAllTabs() }}
              loading={loading}
              style={{
                background: 'linear-gradient(135deg, #37762f 0%, #4a7c59 100%)',
                border: 'none',
                fontWeight: 600,
                boxShadow: '0 4px 14px rgba(55, 118, 47, 0.28)'
              }}
              title="Refresh all tabs data"
            >
              Refresh all tabs
            </Button>
            {Object.values(tabLoading).some(Boolean) && (
              <span style={{ fontSize: 13, color: '#64748b' }}>Refreshing…</span>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced JSPA Statistics Section */}
      <h2 className="rp-section-title">
        <BarChartOutlined style={{ fontSize: '1.1em' }} aria-hidden />
        All refund request dashboard
      </h2>

      {/* Enhanced Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 'clamp(12px, 2vw, 20px)' }}>
        <Col xs={24} sm={12} md={6}>
          <Card className="rp-stat-card" style={{ height: '100%' }}>
            <Statistic
              title="Amount Refund Requested (RWF)"
              value={loading && comprehensiveMeta?.stats?.totalAmountRefundRequests == null ? 'Loading...' : (comprehensiveMeta?.stats?.totalAmountRefundRequests ?? 0)}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#1890ff' }}
              formatter={(value) => typeof value === 'string' ? value : Number(value).toLocaleString()}
              loading={loading && comprehensiveMeta?.stats?.totalAmountRefundRequests == null}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="rp-stat-card" style={{ height: '100%' }}>
            <Statistic
              title="Bid security Paid Amount (RWF)"
              value={loading && comprehensiveMeta?.stats?.allTotalAmountPaidFromBidSecurity == null ? 'Loading...' : (comprehensiveMeta?.stats?.allTotalAmountPaidFromBidSecurity ?? comprehensiveStats?.totalAmount ?? 0)}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#52c41a' }}
              formatter={(value) => typeof value === 'string' ? value : Number(value).toLocaleString()}
              loading={loading && comprehensiveMeta?.stats?.allTotalAmountPaidFromBidSecurity == null}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="rp-stat-card" style={{ height: '100%' }}>
            <Statistic
              title="Processed vs Received Rate"
              value={comprehensiveMeta?.stats?.processedVsReceived ?? comprehensiveStats?.processedVsReceived ?? 0}
              prefix={<BarChartOutlined />}
              valueStyle={{ color: '#fa8c16' }}
              suffix="%"
              formatter={(value) => `${Number(value).toFixed(1)}`}
              loading={loading && comprehensiveMeta?.stats?.processedVsReceived == null}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="rp-stat-card" style={{ height: '100%' }}>
            <Statistic
              title="Completion Rate"
              value={comprehensiveMeta?.stats?.completionRate ?? comprehensiveStats?.completionRate ?? 0}
              suffix="%"
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
              formatter={(value) => `${Number(value).toFixed(1)}`}
              loading={loading && comprehensiveMeta?.stats?.completionRate == null}
            />
          </Card>
        </Col>
        </Row>

      {/* Additional Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 'clamp(12px, 2vw, 20px)' }}>
        <Col xs={24} sm={12} md={6}>
          <Card className="rp-stat-card" style={{ height: '100%' }}>
            <Statistic
              title="Total Amount/Refund Requests Received (RWF)"
              value={loading && comprehensiveMeta?.stats?.totalAmountRefundRequestsReceived == null ? 'Loading...' : (comprehensiveMeta?.stats?.totalAmountRefundRequestsReceived ?? 0)}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#1890ff' }}
              formatter={(value) => typeof value === 'string' ? value : Number(value).toLocaleString()}
              loading={loading && comprehensiveMeta?.stats?.totalAmountRefundRequestsReceived == null}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="rp-stat-card" style={{ height: '100%' }}>
            <Statistic
              title="Pending Refund Request Amount (RWF)"
              value={loading && comprehensiveMeta?.stats?.pendingRefundRequestAmount == null ? 'Loading...' : (comprehensiveMeta?.stats?.pendingRefundRequestAmount ?? comprehensiveMeta?.stats?.remainAmountToBeRefunded ?? 0)}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#fa8c16' }}
              formatter={(value) => typeof value === 'string' ? value : Number(value).toLocaleString()}
              loading={loading && comprehensiveMeta?.stats?.pendingRefundRequestAmount == null}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="rp-stat-card" style={{ height: '100%' }}>
            <Statistic
              title="Downloaded for refund amount (RWF)"
              value={loading && comprehensiveMeta?.stats?.totalAmountDownloadedForRefund == null ? 'Loading...' : (comprehensiveMeta?.stats?.totalAmountDownloadedForRefund ?? 0)}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#52c41a' }}
              formatter={(value) => typeof value === 'string' ? value : Number(value).toLocaleString()}
              loading={loading && comprehensiveMeta?.stats?.totalAmountDownloadedForRefund == null}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="rp-stat-card" style={{ height: '100%' }}>
            <Statistic
              title="Remaining Amount for being refunded Rate"
              value={loading && comprehensiveMeta?.stats?.percentageAmountRemain == null ? 'Loading...' : (comprehensiveMeta?.stats?.percentageAmountRemain ?? 0)}
              prefix={<BarChartOutlined />}
              valueStyle={{ color: '#f5222d' }}
              suffix="%"
              formatter={(value) => typeof value === 'string' ? value : `${Number(value).toLocaleString()}`}
              loading={loading && comprehensiveMeta?.stats?.percentageAmountRemain == null}
            />
          </Card>
        </Col>
      </Row>

      {/* Comprehensive Report Breakdown - Show for all tabs when comprehensive data is available */}
      {(comprehensiveMeta != null || loading) && (
        <>

          {/* Bank Breakdown */}
          <Row gutter={[16, 16]} style={{ marginBottom: 'clamp(12px, 2vw, 20px)' }}>
            <Col xs={24}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, color: '#37762f', fontWeight: 'bold' }}>
                  <BarChartOutlined style={{ marginRight: '8px' }} />
                  Bank Distribution
                </h3>
                <Button
                  type="text"
                  icon={showBankDistribution ? <UpOutlined /> : <DownOutlined />}
                  // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
                  onClick={() => setShowBankDistribution(!showBankDistribution)}
                  style={{ color: '#37762f' }}
                >
                  {showBankDistribution ? 'Hide' : 'Show'} Details
                </Button>
              </div>
            </Col>
            {showBankDistribution && (
              loading
                ? (
                  <Col xs={24} style={{ textAlign: 'center', padding: '20px' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: '10px', color: '#666' }}>Loading bank distribution...</div>
                  </Col>
                  )
                : (
                    Object.entries(bankBreakdown).slice(0, 6).map(([bank, bankCount]) => (
                      <Col xs={24} sm={12} md={8} key={bank}>
                        <Card className="rp-stat-card">
                          <Statistic
                            title={bank}
                            value={bankCount}
                            prefix={<Tag color="blue" />}
                            valueStyle={{ color: '#52c41a' }}
                          />
                        </Card>
                      </Col>
                    ))
                  )
            )}
          </Row>

        </>
      )}

      {/* Comprehensive Report Summary - Show for all tabs when comprehensive data is available */}
      {(comprehensiveMeta != null || loading) && (
        <Row gutter={[16, 16]} style={{ marginBottom: 'clamp(12px, 2vw, 20px)' }}>
          <Col xs={24}>
            <Card
              className="rp-stat-card"
              title={
                <span style={{ color: '#37762f', fontWeight: 'bold' }}>
                  <BarChartOutlined style={{ marginRight: '8px' }} />
                  Status Distribution Report Summary
                </span>
              }
              style={{ backgroundColor: '#fafafa' }}
            >
              <Row gutter={[12, 16]}>
                <Col xs={24} sm={12} md={8} lg={4}>
                    <Statistic
                      title="Total Bid Securities"
                      value={loading && comprehensiveMeta?.stats?.totalBidSecurities == null ? '…' : (comprehensiveMeta?.stats?.totalBidSecurities ?? 0)}
                      formatter={(value) => typeof value === 'string' ? value : Number(value).toLocaleString()}
                      valueStyle={{ color: '#1890ff' }}
                      prefix={<FileTextOutlined />}
                      loading={loading && comprehensiveMeta?.stats?.totalBidSecurities == null}
                    />
                </Col>
                <Col xs={24} sm={12} md={8} lg={4}>
                  <Statistic
                    title="Received Requests"
                    value={loading && comprehensiveMeta?.stats?.receivedRequests == null ? '…' : (comprehensiveMeta?.stats?.receivedRequests ?? 0)}
                    formatter={(value) => typeof value === 'string' ? value : Number(value).toLocaleString()}
                    valueStyle={{ color: '#52c41a' }}
                    prefix={<InfoCircleOutlined />}
                    loading={loading && comprehensiveMeta?.stats?.receivedRequests == null}
                  />
                </Col>
                <Col xs={24} sm={12} md={8} lg={4}>
                  <Statistic
                    title="Pending Requests"
                    value={loading && comprehensiveMeta?.stats?.pendingRequests == null ? '…' : (comprehensiveMeta?.stats?.pendingRequests ?? 0)}
                    formatter={(value) => typeof value === 'string' ? value : Number(value).toLocaleString()}
                    valueStyle={{ color: '#fa8c16' }}
                    prefix={<ClockCircleOutlined />}
                    loading={loading && comprehensiveMeta?.stats?.pendingRequests == null}
                  />
                </Col>
                <Col xs={24} sm={12} md={8} lg={4}>
                  <Statistic
                    title="Failed Requests"
                    value={loading && comprehensiveMeta?.stats?.failedRequests == null ? '…' : (comprehensiveMeta?.stats?.failedRequests ?? 0)}
                    formatter={(value) => typeof value === 'string' ? value : Number(value).toLocaleString()}
                    valueStyle={{ color: '#ff4d4f' }}
                    prefix={<CloseCircleOutlined />}
                    loading={loading && comprehensiveMeta?.stats?.failedRequests == null}
                  />
                </Col>
                <Col xs={24} sm={12} md={8} lg={4}>
                  <Statistic
                    title="Downloaded To Be Refunded"
                    value={loading && comprehensiveMeta?.stats?.downloadedToBeRefundedRequests == null ? '…' : (comprehensiveMeta?.stats?.downloadedToBeRefundedRequests ?? 0)}
                    formatter={(value) => typeof value === 'string' ? value : Number(value).toLocaleString()}
                    valueStyle={{ color: '#1890ff' }}
                    prefix={<DownloadOutlined />}
                    loading={loading && comprehensiveMeta?.stats?.downloadedToBeRefundedRequests == null}
                  />
                </Col>
                <Col xs={24} sm={12} md={8} lg={4}>
                  <Statistic
                    title="Approved Requests"
                    value={loading && comprehensiveMeta?.stats?.approvedRequests == null ? '…' : (comprehensiveMeta?.stats?.approvedRequests ?? 0)}
                    formatter={(value) => typeof value === 'string' ? value : Number(value).toLocaleString()}
                    valueStyle={{ color: '#52c41a' }}
                    prefix={<CheckCircleOutlined />}
                    loading={loading && comprehensiveMeta?.stats?.approvedRequests == null}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      )}

      <div className="rp-filters" style={{ marginBottom: 'clamp(14px, 2vw, 22px)' }}>
        {/* Filter header row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: showAdvancedFilters ? 12 : 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FilterOutlined style={{ color: '#37762f' }} />
            <span style={{ fontWeight: 700, fontSize: 'clamp(13px, 1.4vw, 15px)', color: '#1f2937' }}>
              Filters
            </span>
            {(fromDate !== '' || toDate !== '' || filterStatus !== '' || selectedReportType !== 'all' || searchText !== '') && (
              <Tag color="green" style={{ marginLeft: 4 }}>Active</Tag>
            )}
          </div>
          <Space size="small" wrap>
            <Button
              type="default"
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => { void refreshAllTabs() }}
              loading={Object.values(tabLoading).some(Boolean)}
              style={{ fontSize: '12px' }}
            >
              Refresh
            </Button>
            <Button
              type={showAdvancedFilters ? 'primary' : 'default'}
              size="small"
              icon={<FilterOutlined />}
              onClick={() => { setShowAdvancedFilters(!showAdvancedFilters) }}
              style={{ fontSize: '12px' }}
              ghost={showAdvancedFilters}
            >
              {showAdvancedFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </Space>
        </div>

        {/* Advanced Filters Panel */}
        {showAdvancedFilters && (
          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <Alert
              message="Search & Filter"
              description="Narrow down results by keyword, date range, status, or request type. Click Search to apply."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Form
              form={form}
              layout="vertical"
              onFinish={handleFilterSearch}
              style={{ width: '100%' }}
            >
              <Row gutter={[12, 12]} align="bottom">
                <Col xs={24} sm={24} md={12} lg={8} xl={7}>
                  <Form.Item
                    name="search"
                    label={<span style={{ fontWeight: 500, fontSize: '12px' }}>Keyword Search</span>}
                    style={{ marginBottom: 0 }}
                  >
                    <Input
                      placeholder="Name, ref number, account…"
                      allowClear
                      prefix={<SearchOutlined style={{ color: '#bbb' }} />}
                      value={searchText}
                      onChange={(e) => { setSearchText(e.target.value) }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12} lg={8} xl={7}>
                  <Form.Item
                    name="range_picker"
                    label={<span style={{ fontWeight: 500, fontSize: '12px' }}>Date Range</span>}
                    style={{ marginBottom: 0 }}
                  >
                    <RangePicker
                      allowClear
                      style={{ width: '100%' }}
                      onChange={(value, valueString) => {
                        if (value != null && valueString != null && valueString.length === 2) {
                          setFromDate(valueString[0])
                          setToDate(valueString[1])
                        } else {
                          setFromDate('')
                          setToDate('')
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={5} xl={4}>
                  <Form.Item
                    name="filterStatus"
                    label={<span style={{ fontWeight: 500, fontSize: '12px' }}>Status</span>}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      placeholder="All statuses"
                      allowClear
                      style={{ width: '100%' }}
                      onChange={(value) => { setFilterStatus(value ?? '') }}
                    >
                      <Select.Option value="PENDING">Pending Refund</Select.Option>
                      <Select.Option value="INITIATED">Initiated Refund</Select.Option>
                      <Select.Option value="COMPLETED">Completed Refund</Select.Option>
                      <Select.Option value="Downloaded to be Refunded">Downloaded to be Refunded</Select.Option>
                      <Select.Option value="Bank Transfer Failed">Bank Transfer Failed</Select.Option>
                      <Select.Option value="RECEIVED REQUEST">Received Request</Select.Option>
                      <Select.Option value="REJECTED">Rejected</Select.Option>
                      <Select.Option value="PUBLIC TREASURY">Public Treasury</Select.Option>
                      <Select.Option value="REFUNDED">Refunded</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={5} xl={4}>
                  <Form.Item
                    name="reportType"
                    label={<span style={{ fontWeight: 500, fontSize: '12px' }}>Request Type</span>}
                    initialValue="all"
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      style={{ width: '100%' }}
                      onChange={(value) => { setSelectedReportType(value ?? 'all') }}
                    >
                      <Select.Option value="all">All Types</Select.Option>
                      <Select.Option value="refund">Bidder Refund</Select.Option>
                      <Select.Option value="bailiff">Bailiff Refund</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8} lg={6} xl={4} style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <Form.Item style={{ marginBottom: 0, width: '100%' }}>
                    <Space wrap style={{ width: '100%' }}>
                      <Button
                        type="primary"
                        htmlType="submit"
                        icon={<SearchOutlined />}
                        style={{
                          background: '#37762f',
                          borderColor: '#37762f',
                          fontWeight: 600,
                          minWidth: 100
                        }}
                      >
                        Search
                      </Button>
                      <Button
                        onClick={clearFilters}
                        icon={<ReloadOutlined />}
                        style={{ minWidth: 90 }}
                      >
                        Clear
                      </Button>
                    </Space>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </div>
        )}
      </div>

      {/* Download Buttons */}
      <div
        className="actions"
        style={{
          marginBottom: 'clamp(14px, 2vw, 22px)',
          width: '100%',
          border: '1px solid var(--refund-theme-border, #e5e7eb)',
          borderRadius: '14px',
          padding: 'clamp(12px, 2vw, 16px)',
          background: 'linear-gradient(180deg, #ffffff 0%, var(--refund-theme-primary-soft, #dff0de) 35%, #f8fafc 100%)',
          boxShadow: '0 4px 14px rgba(55, 118, 47, 0.08)'
        }}
      >
        <div style={{ marginBottom: 10 }}>
          <div style={{ color: '#111827', fontWeight: 700, fontSize: '14px' }}>
            Export Report
          </div>
          <div style={{ color: '#6b7280', fontSize: '12px' }}>
            Download the currently filtered data in your preferred format.
          </div>
        </div>
        <Space wrap size="small" style={{ width: '100%' }}>
          <Tooltip title="Download CSV file">
            <Button
              onClick={() => { void downloadFilteredCSV() }}
              type="primary"
              icon={<FileTextOutlined />}
              size="middle"
              style={{ backgroundColor: '#16a34a', borderColor: '#16a34a', fontWeight: 600 }}
            >
              Download CSV
            </Button>
          </Tooltip>
          <Tooltip title="Download Excel file">
            <Button
              onClick={() => { void downloadFilteredExcel() }}
              type="primary"
              icon={<FileExcelOutlined />}
              size="middle"
              style={{ backgroundColor: '#0284c7', borderColor: '#0284c7', fontWeight: 600 }}
            >
              Download Excel
            </Button>
          </Tooltip>
          <Tooltip title="Download PDF file">
            <Button
              onClick={() => { void downloadFilteredPDF() }}
              type="primary"
              icon={<FilePdfOutlined />}
              size="middle"
              style={{ backgroundColor: '#dc2626', borderColor: '#dc2626', fontWeight: 600 }}
            >
              Download PDF
            </Button>
          </Tooltip>
        </Space>
      </div>

      {/* Summary Section for All Tabs */}
      <div style={{ marginBottom: 16, width: '100%' }}>
        <div style={{
          background: 'linear-gradient(135deg, #f6faf7 0%, var(--refund-theme-primary-soft, #dff0de) 100%)',
          borderRadius: 12,
          boxShadow: '0 2px 10px rgba(55, 118, 47, 0.1)',
          padding: '12px 20px',
          border: '1px solid rgba(55, 118, 47, 0.2)',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DollarOutlined style={{ fontSize: 16, color: '#37762f' }} />
            <span style={{ fontSize: 'clamp(12px, 1.4vw, 14px)', color: '#555', fontWeight: 500 }}>
              Combined Bidders &amp; Bailiffs Total:
            </span>
            <span style={{ fontSize: 'clamp(14px, 1.6vw, 16px)', fontWeight: 700, color: '#37762f' }}>
              {tabConfigurations.find(tab => tab.key === 'comprehensive')?.data.length?.toLocaleString() ?? '0'}
            </span>
          </div>
          {comprehensiveMeta?.stats != null && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <span style={{ fontSize: 'clamp(12px, 1.2vw, 13px)', color: '#555' }}>
                  Approved: <strong style={{ color: '#52c41a' }}>{(comprehensiveMeta.stats.approvedRequests ?? 0).toLocaleString()}</strong>
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ClockCircleOutlined style={{ color: '#fa8c16' }} />
                <span style={{ fontSize: 'clamp(12px, 1.2vw, 13px)', color: '#555' }}>
                  Pending: <strong style={{ color: '#fa8c16' }}>{(comprehensiveMeta.stats.pendingRequests ?? 0).toLocaleString()}</strong>
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Refresh Status Indicator */}
      {Object.values(tabLoading).some(Boolean) && (
        <div style={{
          margin: '16px 0',
          padding: '12px 16px',
          backgroundColor: '#e6f7ff',
          border: '1px solid #91d5ff',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <LoadingOutlined style={{ color: '#1890ff' }} />
          <span style={{ color: '#1890ff', fontWeight: '500' }}>
            Refreshing data for: {Object.entries(tabLoading)
            .filter(([_, isLoading]) => isLoading)
            .map(([tabName, _]) => tabName.replace('_', ' ').toUpperCase())
            .join(', ')}
          </span>
        </div>
      )}

      <Card
        className="rp-main-card"
        title={
          <span style={{
            background: 'var(--refund-theme-primary-soft, #dff0de)',
            color: '#111827',
            padding: '10px 18px',
            borderRadius: '10px',
            fontWeight: 600,
            fontSize: 'clamp(0.95rem, 1.8vw, 1.05rem)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            border: '1px solid rgba(55, 118, 47, 0.2)'
          }}>
            <BarChartOutlined style={{ color: 'var(--refund-theme-primary, #37762f)', fontSize: '1.15em' }} aria-hidden />
            Bailiffs &amp; bidders refund requests
          </span>
        }
        style={{ marginTop: '12px', width: '100%' }}
        headStyle={{
          background: 'transparent',
          borderRadius: '14px 14px 0 0',
          padding: '12px 16px'
        }}
      >
        <Tabs
          onChange={(key) => { void handleTabChange(key) }}
          type="card"
          size="middle"
          defaultActiveKey="comprehensive"
          activeKey={activeTab}
          tabBarGutter={4}
          moreIcon={<DownOutlined />}
          tabBarStyle={{
            backgroundColor: 'rgba(55, 118, 47, 0.06)',
            padding: '4px 8px 0',
            margin: '0 0 12px 0',
            borderRadius: '10px 10px 0 0',
            borderBottom: '1px solid rgba(55, 118, 47, 0.12)',
            flexWrap: 'nowrap',
            overflowX: 'auto'
          }}
          tabBarExtraContent={
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13.5px',
              color: '#666'
            }}>
              <ReloadOutlined
                style={{
                  cursor: 'pointer',
                  color: tabLoading[activeTab] ? '#1890ff' : '#666',
                  animation: tabLoading[activeTab] ? 'spin 1s linear infinite' : 'none'
                }}
                onClick={() => {
                  // Force refresh current tab data
                  setLastDataFetch(prev => ({ ...prev, [activeTab]: 0 }))
                  void handleTabChange(activeTab)
                }}
                title="Refresh current tab data"
              />
              {tabLoading[activeTab] && <span>Loading...</span>}
              <Button
                type="link"
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => { void refreshAllTabs() }}
                loading={Object.values(tabLoading).some(Boolean)}
                style={{
                  fontSize: '13.5px',
                  padding: '0 4px',
                  color: Object.values(tabLoading).some(Boolean) ? '#1890ff' : '#666'
                }}
                title="Refresh all tabs data"
              >
                Refresh All
              </Button>
            </div>
          }
          items={tabConfigurations.map((tabConfig) => {
            // Debug logging for table data
            if (tabConfig.key === 'comprehensive') {
              // eslint-disable-next-line no-console
              console.log('🔍 Comprehensive Tab Debug:')
              // eslint-disable-next-line no-console
              console.log('📊 Tab data length:', tabConfig.data.length)
              // eslint-disable-next-line no-console
              console.log('📊 Tab columns length:', tabConfig.columns.length)
              // eslint-disable-next-line no-console
              console.log('📊 Tab data sample:', tabConfig.data.slice(0, 2))
              // eslint-disable-next-line no-console
              console.log('📊 Tab columns:', tabConfig.columns.map(col => col.title))
            }
            const isTabLoading = tabLoading[tabConfig.key] ?? false
            const hasData = tabConfig.data.length > 0

            return {
              key: tabConfig.key,
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {isTabLoading && <LoadingOutlined style={{ fontSize: '12px' }} />}
                  <span>{tabConfig.title}</span>
                  <Tag
                    color={hasData ? 'success' : 'default'}
                    style={{ marginLeft: '4px', fontSize: '12px' }}
                  >
                    {tabConfig.data.length}
                  </Tag>
                </div>
              ),
              children: (
                <div style={{ position: 'relative', width: '100%' }}>
                  {/* Data freshness indicator */}
                  <div style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '0',
                    zIndex: 10,
                    fontSize: '12px',
                    color: '#64748b',
                    background: '#fff',
                    padding: '4px 8px',
                    borderRadius: '8px',
                    border: '1px solid var(--refund-theme-border, #e5e7eb)',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                  }}>
                    {lastDataFetch[tabConfig.key] !== undefined
                      ? `Updated ${Math.round((Date.now() - lastDataFetch[tabConfig.key]) / 1000)}s ago`
                      : 'Never updated'
                    }
                  </div>
                  <Spin spinning={isTabLoading || loading} indicator={antIcon} tip="Loading data...">
                    <Table
                      columns={tabConfig.columns}
                      dataSource={tabConfig.data}
                      rowKey={(record) => {
                        const key = (record as any).key ?? (record as any).id ?? (record as any).refund_request_id ?? (record as any).bailiff_refund_request_id ?? (record as any).bid_security_payment_id
                        return key != null ? String(key) : Math.random().toString()
                      }}
                      onChange={handleTableChange}
                      scroll={{ x: 'max-content' }}
                      pagination={{
                        ...pagination,
                        total: tabConfig.data.length,
                        pageSize: 10,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        hideOnSinglePage: false,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showLessItems: true,
                        responsive: true
                      }}
                      loading={isTabLoading || loading}
                      bordered
                      size="middle"
                      className="app-table rp-data-table"
                      locale={{
                        emptyText: hasData ? 'No records match this view.' : 'No data yet. Click Refresh to load report data.'
                      }}
                    />
                  </Spin>
                </div>
              )
            }
          })}
        />
      </Card>
      </div>
    </div>
  )
}

export default Reports
